import CommunityNavBadge from "../components/CommunityNavBadge";
import BioIndexUploadMobileNav from "../components/BioIndexUploadMobileNav";
import "../styles/BioIndexCommunityMobile.css";
import "../styles/BioIndexUpload.css";
import "../styles/BioIndexUploadFlow.css";
import "../styles/BioIndexUploadMobilePolish.css";
/*
 * BioIndex
 * Copyright (c) 2026 BioIndex(TM). All rights reserved.
 * Unauthorized copying, scraping, redistribution, or commercial reuse is prohibited.
 */

// ===================== Imports =====================
//
// Core React hooks for state, lifecycle, and memoized callbacks
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

// Client-side routing for the page navigation
import { Link } from "react-router-dom";

// Drag-and-drop file upload handling
import { useDropzone } from "react-dropzone";

// EXIF metadata extraction (dates, GPS from images)
import * as exifr from "exifr";

// Firebase services (database, storage, authentication)
import { db, storage, auth } from "../firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import { onAuthStateChanged } from "firebase/auth";

// Import page modals
import LocationPickerModal from "../components/LocationPickerModal";
import SearchModal from "../components/SearchModal";
import ForumSpeciesPhotoCard from "../components/ForumSpeciesPhotoCard";

// Import logo
import geminiLogo from "../assets/gemini.png";
import inatLogo from "../assets/inat.png";

// Firestore methods for reading/writing species documents
import {
  doc, // reference to a specific document
  getDoc, // read a single document
  setDoc, // create/overwrite a document
  updateDoc, // update existing document fields
  getDocs, // read multiple documents (collections)
  collection, // reference to a collection
  writeBatch, // save all pending collection changes together
} from "firebase/firestore";

// Firebase Storage methods for uploading images
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";

// Predefined labels for "form" (e.g., male, female, flower, etc.)
import { resolveLifeformKingdom } from "../utils/formOptions";
import LifeformPicker from "../components/LifeformPicker";

// Taxonomic mapping: family -> genera (used to infer family from genus)
import {
  generaByFamily,
  familiesByOrder,
  ordersByClass,
  classesByPhylum,
  phylaByKingdom,
} from "../utils/taxonomy";

// Unique ID generator for each uploaded form/image
import { nanoid } from "nanoid";

// Local species database for autocomplete suggestions
import { speciesNames } from "../utils/speciesNames";
import { iconUrl, defaultImg } from "../utils/icons";

// Predefined day options for date selection
// Includes "NA" as a fallback for unknown/missing dates
// Generates "01" -> "31" using Array.from and zero-padding

// ===================== Constants =====================
const DAYS = [
  "NA",
  ...Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, "0")),
];

// Predefined month options
// Explicitly listed to guarantee consistent formatting ("01" -> "12")
// Avoids any off-by-one or formatting issues from dynamic generation
const MONTHS = [
  { value: "NA", label: "NA" },
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

// Predefined year options
// Includes "NA" for missing values
// Dynamically generates the last 80 years from the current year backwards
// Ensures the dropdown stays up-to-date without manual edits
const YEARS = [
  "NA",
  ...Array.from({ length: 80 }, (_, i) => String(new Date().getFullYear() - i)),
];

const TUTORIAL_STEPS = [
  {
    title: "Identify the species",
    text: "Enter either the scientific or common name. Select a BioIndex database match to fill both names automatically. If the name you are looking for does not appear in the suggestions, we do not have it yet in our database! You can still type in any scientific name, but make sure that the name is spelled properly and is currently in use. If the database does not include the common name you use, type it in yourself.",
  },
  {
    title: "Use the identification tools",
    text: "If you need a name suggestion, complete the Date, Location, and Lifeform fields when possible, then select Suggest ID with Gemini. Gemini considers the photograph together with those details. Review its suggestion before accepting it. Once you have typed in a scientific name, or Gemini suggests one, you can use the 'Check on iNaturalist' button to open a separate search and compare the proposed identification with iNaturalist records.",
  },
  {
    title: "Review existing photos",
    text: "If the scientific name already exists in your BioIndex, an existing-photos panel appears beneath the photograph. Open it to view the photos you previously uploaded for that species and, when necessary, mark any of them for deletion after this batch is uploaded.",
  },
  {
    title: "Confirm date and location",
    text: "Check the date recovered from the photograph and use NA for any unknown part. Opening Location displays a map where you can search for a place, choose one of your recent locations, use your current location, or select a point directly on the map. You can switch between map and satellite views, review the selected place and coordinates, and then select Use Location.",
  },
  {
    title: "Describe what is visible",
    text: "Use Lifeform to describe what the photograph shows, such as an adult animal, seedling, flower, or fungal fruiting body. BioIndex offers options for the species kingdom. If the kingdom is unknown, select it first. These details can help Gemini identify your photograph.",
  },
  {
    title: "Add optional details",
    text: "Select Variant or Notes whenever the record needs more context. Variant records a subspecies, variety, cultivar, breed, or morph. Notes preserve observations about behavior, habitat, identification features, or other field information.",
  },
  {
    title: "Review and upload",
    text: "Complete the process for every photo. If multiple photos share an identification, date, or location, use Shared Details to apply that information across the batch. Review each record and select Upload when everything is ready. After the upload, you can choose a default photo for previously recorded species and add new photos to collections.",
  },
];

// ===================== Utility Functions =====================
// Ensures a consistent date object structure across the app
// Safely handles missing or partial dates using optional chaining (?.)
// Falls back to "NA" for any undefined component (day/month/year)
function normalizeDate(d) {
  return {
    day: d?.day || "NA",
    month: d?.month || "NA",
    year: d?.year || "NA",
  };
}

// Normalizes a user-provided label (e.g., form type) into two parts:
// 1. A display label (original, trimmed)
// 2. A safe ID (lowercase, URL/Firestore-friendly)
function normalizeLabel(label) {
  const raw = (label || "").trim();

  // If empty, enforce a default label/id pair
  if (!raw) return { label: "Default", id: "default" };

  // Convert to a safe ID:
  // - lowercase
  // - replace non-alphanumeric characters with underscores
  // - trim leading/trailing underscores
  const safeId = raw
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

  // Fallback to "default" if cleaning results in an empty string
  return { label: raw, id: safeId || "default" };
}

// Sorts an array of forms so that the default image appears first
// Does not mutate the original array (creates a shallow copy)
// Uses boolean -> numeric conversion for comparison (true = 1, false = 0)
function sortDefaultFirst(forms) {
  const arr = Array.isArray(forms) ? [...forms] : [];

  arr.sort((a, b) => {
    const ad = a?.isDefault ? 1 : 0;
    const bd = b?.isDefault ? 1 : 0;

    // Higher value (default=true) comes first
    return bd - ad;
  });

  return arr;
}

// Infers the taxonomic family from a given genus
// Iterates through the generaByFamily mapping (family -> [genera])
// Returns the matching family with capitalization normalized (e.g., "fabaceae" -> "Fabaceae")
// If no match is found, returns an empty string
function getFamilyFromGenus(genus) {
  for (const [family, genera] of Object.entries(generaByFamily)) {
    if (genera.includes(genus)) {
      return family.charAt(0).toUpperCase() + family.slice(1);
    }
  }
  return "";
}

function buildTaxonomyParentLookup(groups) {
  const lookup = new Map();

  Object.entries(groups || {}).forEach(([parent, children]) => {
    (children || []).forEach((child) => {
      lookup.set(
        String(child || "")
          .trim()
          .toLowerCase(),
        parent,
      );
    });
  });

  return lookup;
}

const FAMILY_BY_GENUS = buildTaxonomyParentLookup(generaByFamily);
const ORDER_BY_FAMILY = buildTaxonomyParentLookup(familiesByOrder);
const CLASS_BY_ORDER = buildTaxonomyParentLookup(ordersByClass);
const PHYLUM_BY_CLASS = buildTaxonomyParentLookup(classesByPhylum);
const KINGDOM_BY_PHYLUM = buildTaxonomyParentLookup(phylaByKingdom);

function formatTaxonName(value) {
  const cleanValue = String(value || "").trim();
  return cleanValue
    ? cleanValue.charAt(0).toUpperCase() + cleanValue.slice(1)
    : "";
}

function resolveTaxonomyFromGenus(genusValue) {
  const genus = String(genusValue || "").trim();
  const family = FAMILY_BY_GENUS.get(genus.toLowerCase()) || "";
  const order = ORDER_BY_FAMILY.get(String(family).toLowerCase()) || "";
  const className = CLASS_BY_ORDER.get(String(order).toLowerCase()) || "";
  const phylum = PHYLUM_BY_CLASS.get(String(className).toLowerCase()) || "";
  const kingdom = KINGDOM_BY_PHYLUM.get(String(phylum).toLowerCase()) || "";

  return {
    genus: formatTaxonName(genus),
    family: formatTaxonName(family),
    order: formatTaxonName(order),
    class: formatTaxonName(className),
    phylum: formatTaxonName(phylum),
    kingdom: formatTaxonName(kingdom),
  };
}

async function findNewTaxonomyUnlocks(uid, successfulPhotos) {
  const newlyUploadedSpecies = new Map();

  successfulPhotos.forEach((photo) => {
    if (!photo.wasNewToUser || !photo.speciesDocId) return;

    if (!newlyUploadedSpecies.has(photo.speciesDocId)) {
      newlyUploadedSpecies.set(photo.speciesDocId, photo);
    }
  });

  if (!uid || newlyUploadedSpecies.size === 0) return [];

  const speciesSnapshot = await getDocs(
    collection(db, "users", uid, "species"),
  );

  const ranks = ["genus", "family", "order", "class", "phylum", "kingdom"];
  const membersByRank = Object.fromEntries(
    ranks.map((rank) => [rank, new Map()]),
  );
  const taxonomyBySpeciesId = new Map();

  speciesSnapshot.forEach((speciesDocument) => {
    const speciesData = speciesDocument.data() || {};
    const genus =
      String(speciesData.genus || "").trim() ||
      String(speciesData.name || "")
        .trim()
        .split(/\s+/)[0] ||
      "";
    const taxonomy = resolveTaxonomyFromGenus(genus);

    taxonomyBySpeciesId.set(speciesDocument.id, taxonomy);

    ranks.forEach((rank) => {
      const taxonName = taxonomy[rank];
      if (!taxonName) return;

      if (!membersByRank[rank].has(taxonName)) {
        membersByRank[rank].set(taxonName, new Set());
      }

      membersByRank[rank].get(taxonName).add(speciesDocument.id);
    });
  });

  const newSpeciesIds = new Set(newlyUploadedSpecies.keys());
  const unlocks = [];
  const recordedUnlocks = new Set();

  newlyUploadedSpecies.forEach((photo, speciesDocId) => {
    const taxonomy = taxonomyBySpeciesId.get(speciesDocId);
    if (!taxonomy) return;

    ranks.forEach((rank) => {
      const taxonName = taxonomy[rank];
      const unlockKey = `${rank}:${taxonName}`;
      const taxonMembers = membersByRank[rank].get(taxonName);

      if (
        !taxonName ||
        recordedUnlocks.has(unlockKey) ||
        !taxonMembers ||
        !Array.from(taxonMembers).every((memberId) =>
          newSpeciesIds.has(memberId),
        )
      ) {
        return;
      }

      recordedUnlocks.add(unlockKey);
      unlocks.push({
        rank,
        taxonName,
        speciesDocId,
        scientificName: photo.scientificName,
        photoURL: photo.url,
      });
    });
  });

  return unlocks;
}

// Gemini suggestions must identify a species or a confirmed genus. The callable
// may return a rank; older responses without one are accepted at genus level
// only when that genus is present in our taxonomy or species-name catalog.
const KNOWN_HIGHER_TAXA = new Set(
  [familiesByOrder, ordersByClass, classesByPhylum, phylaByKingdom]
    .flatMap((groups) => [
      ...Object.keys(groups),
      ...Object.values(groups).flat(),
    ])
    .map((name) => String(name).toLowerCase()),
);
const KNOWN_GENERA = new Set(
  [
    ...Object.values(generaByFamily).flat(),
    ...speciesNames.map(
      (species) => String(species.scientific || "").split(/\s+/)[0],
    ),
  ].map((name) => String(name).toLowerCase()),
);

function reviewGeminiSuggestion(suggestion) {
  const name = String(
    suggestion?.scientificName || suggestion?.suggestedTaxonName || "",
  )
    .trim()
    .replace(/\s+/g, " ");
  const rank = String(
    suggestion?.rank ||
      suggestion?.suggestedTaxonRank ||
      suggestion?.taxonRank ||
      suggestion?.taxonomicRank ||
      "",
  )
    .trim()
    .toLowerCase();
  const parts = name.split(" ");
  const genus = parts[0] || "";
  const higherRank =
    KNOWN_HIGHER_TAXA.has(name.toLowerCase()) ||
    /(?:aceae|ales|idae|inae|oidea|iformes|phyta|mycota|opsida)$/i.test(name);
  const genusValid =
    /^[A-Z][\p{L}-]+$/u.test(genus) &&
    !KNOWN_HIGHER_TAXA.has(genus.toLowerCase()) &&
    !/(?:aceae|ales|idae|inae|oidea|iformes|phyta|mycota|opsida)$/i.test(genus);
  const speciesValid =
    parts.length === 2 && genusValid && /^[a-z][\p{L}-]+$/u.test(parts[1]);
  const genusOnly =
    parts.length === 1 &&
    genusValid &&
    (rank === "genus" || (!rank && KNOWN_GENERA.has(genus.toLowerCase())));

  if (!higherRank && (rank === "species" || !rank) && speciesValid) {
    return { accepted: true, name, rank: "species" };
  }
  if (!higherRank && genusOnly) {
    return { accepted: true, name, rank: "genus" };
  }
  const normalizedName = name.toLowerCase();
  const inferredRank = Object.keys(familiesByOrder).some(
    (order) => order.toLowerCase() === normalizedName,
  )
    ? "order"
    : Object.keys(generaByFamily).some(
          (family) => family.toLowerCase() === normalizedName,
        ) || /(?:aceae|idae)$/i.test(name)
      ? "family"
      : "an unconfirmed taxon";
  const identifiedRank =
    rank && !["genus", "species"].includes(rank) ? rank : inferredRank;
  return { accepted: false, name, rank: identifiedRank };
}

// Normalizes a scientific name into a Firestore-safe document ID
// - Trims whitespace
// - Converts to lowercase for consistency
// Prevents duplicate documents due to casing or spacing differences
function normalizeSpeciesDocId(sci) {
  return (sci || "").trim().toLowerCase();
}

async function bumpProfileCacheMarkers(uid, sections = {}) {
  if (!uid) return;

  const now = Date.now();
  const sectionUpdatedAt = {};

  if (sections.taxonomy) sectionUpdatedAt.taxonomy = now;
  if (sections.collections) sectionUpdatedAt.collections = now;
  if (sections.quiz) sectionUpdatedAt.quiz = now;
  if (sections.profile) sectionUpdatedAt.profile = now;

  if (Object.keys(sectionUpdatedAt).length === 0) return;

  await setDoc(doc(db, "profile", uid), { sectionUpdatedAt }, { merge: true });
}

// Extracts capture date metadata from an image file using EXIF data
// Attempts to read standard EXIF fields: DateTimeOriginal (preferred) or CreateDate
// Returns a normalized { day, month, year } object formatted as strings ("DD", "MM", "YYYY")
// Falls back to "NA" values if no date is found or parsing fails
async function extractDateFromExif(file) {
  try {
    // Parse only relevant EXIF fields for efficiency
    const exif = await exifr.parse(file, ["DateTimeOriginal", "CreateDate"]);

    // Prefer original capture date, fallback to creation date
    const date = exif?.DateTimeOriginal || exif?.CreateDate;

    // If no valid date found, return placeholder values
    if (!date) return { day: "NA", month: "NA", year: "NA" };

    // Format date components with zero-padding for consistency
    return {
      day: String(date.getDate()).padStart(2, "0"),
      month: String(date.getMonth() + 1).padStart(2, "0"), // JS months are 0-based
      year: String(date.getFullYear()),
    };
  } catch {
    // Handle parsing errors safely without breaking upload flow
    return { day: "NA", month: "NA", year: "NA" };
  }
}

// The browser can suspend JavaScript while a phone screen is locked. Firebase
// may also keep retrying a request after connectivity has failed. These
// deadlines let the page return to its existing per-photo retry flow.
const UPLOAD_IDLE_TIMEOUT_MS = 120000; // No new bytes for two minutes
const UPLOAD_MAX_TRANSFER_MS = 10 * 60 * 1000; // Total time per photo
const UPLOAD_READ_TIMEOUT_MS = 75000;
const UPLOAD_WRITE_TIMEOUT_MS = 120000;

const stoppedUploadError = () =>
  new Error("Upload stopped. Your photo details are preserved. Select Retry Upload when ready.");

// A timeout does not cancel a Firestore request that has already been sent.
// The uploader deliberately uses stable form IDs and checks whether a form
// exists before retrying to avoid saving it twice.
function awaitUploadStep(start, label, timeoutMs, control) {
  return new Promise((resolve, reject) => {
    if (control.cancelled) {
      reject(stoppedUploadError());
      return;
    }

    let settled = false;
    let timerId = null;
    let cancelStep = null;
    const deadline = Date.now() + timeoutMs;

    const finish = (callback, value) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timerId);
      document.removeEventListener("visibilitychange", checkDeadline);
      if (control.cancelCurrent === cancelStep) control.cancelCurrent = null;
      callback(value);
    };
    const checkDeadline = () => {
      if (Date.now() >= deadline) {
        finish(
          reject,
          new Error(`${label} did not respond in time. Check your connection and retry.`),
        );
      }
    };
    cancelStep = () => finish(reject, stoppedUploadError());
    control.cancelCurrent = cancelStep;
    document.addEventListener("visibilitychange", checkDeadline);
    timerId = window.setTimeout(checkDeadline, timeoutMs);

    try {
      Promise.resolve(start()).then(
        (value) => finish(resolve, value),
        (error) => finish(reject, error),
      );
    } catch (error) {
      finish(reject, error);
    }
  });
}

function awaitStorageTransfer(task, onProgress, control) {
  return new Promise((resolve, reject) => {
    let settled = false;
    let intervalId = null;
    let unsubscribe = () => {};
    let cancelStep = null;
    const startedAt = Date.now();
    let lastByteAt = startedAt;
    let highestBytes = 0;

    const finish = (callback, value) => {
      if (settled) return;
      settled = true;
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", checkTransfer);
      if (control.cancelCurrent === cancelStep) control.cancelCurrent = null;
      unsubscribe();
      callback(value);
    };
    const abort = (error) => {
      if (settled) return;
      finish(reject, error);
      // Cancels the actual Storage task rather than merely abandoning its promise.
      task.cancel();
    };
    const checkTransfer = () => {
      if (Date.now() - lastByteAt >= UPLOAD_IDLE_TIMEOUT_MS) {
        abort(new Error("No upload progress for two minutes. Check your connection and retry."));
      } else if (Date.now() - startedAt >= UPLOAD_MAX_TRANSFER_MS) {
        abort(new Error("This photo exceeded the 10-minute transfer limit. Please retry."));
      }
    };
    cancelStep = () => abort(stoppedUploadError());
    control.cancelCurrent = cancelStep;
    if (control.cancelled) {
      cancelStep();
      return;
    }
    document.addEventListener("visibilitychange", checkTransfer);
    intervalId = window.setInterval(checkTransfer, 5000);
    unsubscribe = task.on(
      "state_changed",
      (snapshot) => {
        if (snapshot.bytesTransferred > highestBytes) {
          highestBytes = snapshot.bytesTransferred;
          lastByteAt = Date.now();
        }
        onProgress(snapshot);
      },
      (error) => finish(reject, error),
      () => finish(resolve),
    );
  });
}

// ===================== Main Function =====================

export default function AddSpeciesPage() {
  const functions = getFunctions(undefined, "southamerica-east1");
  const [entries, setEntries] = useState([]); // all uploaded photo entries (core state)
  // Layout-only navigation: every entry stays mounted, and existing controlled
  // inputs continue to update the same entries array used by the upload flow.
  const [activePhotoIndex, setActivePhotoIndex] = useState(0);
  const [photoTrayOpen, setPhotoTrayOpen] = useState(false);
  const mobilePhotoCarouselRef = useRef(null);
  const uploadEditorRef = useRef(null);
  const progressCarouselRef = useRef(null);
  const previewUrlsRef = useRef(new Set());
  // The progress screen and its photo carousel remain visible after an error.
  // The original entries (including successful ones) are retained for retries.
  const [uploadView, setUploadView] = useState("editing");
  const [progressPhotoIndex, setProgressPhotoIndex] = useState(0);
  const [uploadPercents, setUploadPercents] = useState({});

  const selectUploadPhoto = (index) => {
    // Uploaded photos are final. Only unfinished records remain editable.
    if (uploadView === "editing" && entries[index]?.uploadStatus === "success")
      return;
    setActivePhotoIndex(Math.max(0, Math.min(index, entries.length - 1)));
    setPhotoTrayOpen(false);
    setActiveField({});
    // Switching photos on a phone must return to the image and top fields,
    // rather than leaving the user at the bottom of the previous form.
    if (window.matchMedia("(max-width: 767px)").matches) {
      window.requestAnimationFrame(() => {
        uploadEditorRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
    }
  };

  useEffect(() => {
    setActivePhotoIndex((current) =>
      Math.max(0, Math.min(current, entries.length - 1)),
    );
    if (entries.length === 0) setPhotoTrayOpen(false);
  }, [entries.length]);

  // Keep the active thumbnail visible when changing photos with arrows or
  // after removing one. Scroll only the thumbnail strip, never the page.
  useEffect(() => {
    const carousel = mobilePhotoCarouselRef.current;
    if (!carousel) return;

    const selected = carousel.querySelector(
      '[data-upload-photo-active="true"]',
    );
    if (!selected) return;

    const carouselBounds = carousel.getBoundingClientRect();
    const selectedBounds = selected.getBoundingClientRect();
    const padding = 6;
    let delta = 0;

    if (selectedBounds.left < carouselBounds.left + padding) {
      delta = selectedBounds.left - carouselBounds.left - padding;
    } else if (selectedBounds.right > carouselBounds.right - padding) {
      delta = selectedBounds.right - carouselBounds.right + padding;
    }

    if (delta !== 0) {
      carousel.scrollBy({ left: delta, behavior: "smooth" });
    }
  }, [activePhotoIndex, entries.length]);

  const [mode, setMode] = useState("new"); // upload mode: "new" or "replace"
  const [replaceIndex, setReplaceIndex] = useState(null); // target index when replacing an existing form
  const [suggestions, setSuggestions] = useState({}); // autocomplete results per entry index
  const [activeSuggestion, setActiveSuggestion] = useState({}); // highlighted suggestion index (keyboard nav)
  const [activeField, setActiveField] = useState({}); // which field is active (scientific/common)
  const [openExistingIndex, setOpenExistingIndex] = useState(null); // controls modal for existing forms
  const [isUploading, setIsUploading] = useState(false); // global upload state
  const activeUploadControlRef = useRef(null);
  const stopCurrentUpload = () => {
    const control = activeUploadControlRef.current;
    if (!control) return;
    control.cancelled = true;
    control.cancelCurrent?.();
  };
  const [fileLimitError, setFileLimitError] = useState(null); // error for >10 files
  const [uploadError, setUploadError] = useState(null); // upload failure message
  const [uploadSuccess, setUploadSuccess] = useState(false); // upload success flag
  const [showMissingNameAlert, setShowMissingNameAlert] = useState(false);
  const [highlightMissingNames, setHighlightMissingNames] = useState(false);
  const [geminiDialog, setGeminiDialog] = useState(null);
  const [geminiContextWarning, setGeminiContextWarning] = useState(null);
  const [removePhotoDialog, setRemovePhotoDialog] = useState(null);

  const [postUploadDefaultOptions, setPostUploadDefaultOptions] = useState({});
  const [pendingPostUploadDefaults, setPendingPostUploadDefaults] = useState(
    {},
  );
  const [collections, setCollections] = useState([]); // available collection names
  const [uploadedBatchPhotos, setUploadedBatchPhotos] = useState([]);
  const [uploadedBatchSpecies, setUploadedBatchSpecies] = useState([]);
  const [postUploadCommunitySummary, setPostUploadCommunitySummary] = useState(
    [],
  );
  const [postUploadCommunityLoading, setPostUploadCommunityLoading] =
    useState(false);
  const [postUploadCommunityError, setPostUploadCommunityError] = useState("");
  const [postUploadFriendCount, setPostUploadFriendCount] = useState(0);
  const [newTaxonomyUnlocks, setNewTaxonomyUnlocks] = useState([]);
  const [taxonomyUnlockDetailsOpen, setTaxonomyUnlockDetailsOpen] =
    useState(false);
  const [expandedFriendComparisons, setExpandedFriendComparisons] = useState(
    {},
  );
  const [postUploadCarouselIndexes, setPostUploadCarouselIndexes] = useState(
    {},
  );
  const [postUploadCollectionToolsOpen, setPostUploadCollectionToolsOpen] =
    useState(false);
  const [postUploadCollection, setPostUploadCollection] = useState("");
  const [newPostUploadCollection, setNewPostUploadCollection] = useState("");
  const [postUploadCollectionError, setPostUploadCollectionError] =
    useState("");
  const [postUploadCollectionMessage, setPostUploadCollectionMessage] =
    useState("");
  const [pendingPostUploadCollections, setPendingPostUploadCollections] =
    useState([]);
  const [savingPostUploadCollections, setSavingPostUploadCollections] =
    useState(false);
  const [searchOpen, setSearchOpen] = useState(false); // controls the species search modal
  const [voiceOpen, setVoiceOpen] = useState(false);

  // Shared Details is a deliberate three-step flow. Nothing is written to
  // entries until the user confirms the field, recipients, and value.
  const [sharedDetailsOpen, setSharedDetailsOpen] = useState(false);
  const [sharedStep, setSharedStep] = useState(1);
  const [sharedField, setSharedField] = useState("species");
  const [sharedSelectedIds, setSharedSelectedIds] = useState([]);
  const [sharedModalError, setSharedModalError] = useState("");
  const [sharedAppliedMessage, setSharedAppliedMessage] = useState("");
  const [collectionTotals, setCollectionTotals] = useState(null);
  const [collectionTotalsError, setCollectionTotalsError] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(null); // null = tutorial closed
  const tutorialModalRef = useRef(null);
  const tutorialLaunchRef = useRef(null);
  const tutorialOpen = tutorialStep !== null;
  const [globalSpeciesQuery, setGlobalSpeciesQuery] = useState("");
  const [globalCommonName, setGlobalCommonName] = useState("");
  const [globalSpeciesSuggestions, setGlobalSpeciesSuggestions] = useState([]);

  const [globalDate, setGlobalDate] = useState({
    day: "NA",
    month: "NA",
    year: "NA",
  }); // shared date when sameDate = true

  const [globalLocation, setGlobalLocation] = useState(""); // shared location
  const [globalLocationData, setGlobalLocationData] = useState(null); // structured shared location
  const [locationModal, setLocationModal] = useState(null); // map picker modal state

  // Read the authoritative collection rather than displaying estimated
  // counters. Recheck after uploads complete, including partial success.
  useEffect(() => {
    let cancelled = false;
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        setCollectionTotals(null);
        setCollectionTotalsError(false);
        return;
      }
      setCollectionTotalsError(false);
      getDocs(collection(db, "users", user.uid, "species"))
        .then((snapshot) => {
          if (cancelled) return;
          const uniqueSpecies = new Set();
          let photoCount = 0;
          snapshot.forEach((item) => {
            const data = item.data() || {};
            const scientificName = String(data.name || "").trim();
            if (!scientificName || !String(data.genus || "").trim()) return;
            uniqueSpecies.add(
              scientificName.toLowerCase().replace(/\s+/g, " "),
            );
            photoCount += Array.isArray(data.forms) ? data.forms.length : 0;
          });
          setCollectionTotals({
            species: uniqueSpecies.size,
            photos: photoCount,
          });
        })
        .catch((error) => {
          if (cancelled) return;
          console.warn("Unable to read collection totals:", error);
          setCollectionTotalsError(true);
        });
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [uploadSuccess, uploadedBatchPhotos.length]);

  // Prevent page scrolling while the shared-detail dialog is active.
  useEffect(() => {
    if (!sharedDetailsOpen || locationModal?.scope === "global") return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const handleEscape = (event) => {
      if (event.key === "Escape") setSharedDetailsOpen(false);
    };
    window.addEventListener("keydown", handleEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleEscape);
    };
  }, [sharedDetailsOpen, locationModal]);

  // Keep the tutorial on top of the upload form without shifting page layout.
  // Trap keyboard focus inside its dialog and return focus to the launcher.
  useEffect(() => {
    if (!tutorialOpen) return;

    const previouslyFocused = document.activeElement;
    const tutorialLauncher = tutorialLaunchRef.current;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    tutorialModalRef.current?.querySelector("[data-tutorial-close]")?.focus();

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setTutorialStep(null);
        return;
      }
      if (event.key !== "Tab") return;

      const focusable = Array.from(
        tutorialModalRef.current?.querySelectorAll(
          'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ) || [],
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener("keydown", onKeyDown);
      if (previouslyFocused?.isConnected) previouslyFocused.focus();
      else tutorialLauncher?.focus();
    };
  }, [tutorialOpen]);

  const selectedPostUploadPhotoCount =
    postUploadCollection && postUploadCollection !== "__new__"
      ? uploadedBatchPhotos.filter((photo) =>
          Array.isArray(photo.collections)
            ? photo.collections.includes(postUploadCollection)
            : false,
        ).length
      : 0;

  const emptyPendingPostUploadCollections = pendingPostUploadCollections.filter(
    (collectionName) =>
      !uploadedBatchPhotos.some((photo) =>
        Array.isArray(photo.collections)
          ? photo.collections.includes(collectionName)
          : false,
      ),
  );

  const selectedCollectionNeedsPhoto =
    pendingPostUploadCollections.includes(postUploadCollection) &&
    selectedPostUploadPhotoCount === 0;

  const resetSharedDetails = useCallback(() => {
    setSharedDetailsOpen(false);
    setSharedStep(1);
    setSharedField("species");
    setSharedSelectedIds([]);
    setSharedModalError("");
    setSharedAppliedMessage("");
    setGlobalSpeciesQuery("");
    setGlobalCommonName("");
    setGlobalSpeciesSuggestions([]);
    setGlobalDate({ day: "NA", month: "NA", year: "NA" });
    setGlobalLocation("");
    setGlobalLocationData(null);
    setLocationModal(null);
  }, []);

  // Handles drag-and-drop file input and initializes entry objects
  const onDrop = useCallback(
    async (acceptedFiles) => {
      setUploadError(null); // reset errors
      setUploadView("editing");
      setProgressPhotoIndex(0);
      setUploadPercents({});
      setShowMissingNameAlert(false);
      setHighlightMissingNames(false);
      setGeminiDialog(null);
      setGeminiContextWarning(null);
      setRemovePhotoDialog(null);
      setUploadSuccess(false);
      setUploadedBatchPhotos([]);
      setUploadedBatchSpecies([]);
      setPostUploadDefaultOptions({});
      setPendingPostUploadDefaults({});
      setPostUploadCommunitySummary([]);
      setPostUploadCommunityLoading(false);
      setPostUploadCommunityError("");
      setPostUploadFriendCount(0);
      setNewTaxonomyUnlocks([]);
      setTaxonomyUnlockDetailsOpen(false);
      setExpandedFriendComparisons({});
      setPostUploadCarouselIndexes({});
      setPostUploadCollectionToolsOpen(false);
      setPostUploadCollection("");
      setNewPostUploadCollection("");
      setPostUploadCollectionError("");
      setPostUploadCollectionMessage("");
      setPendingPostUploadCollections([]);
      setFileLimitError(null);
      resetSharedDetails();

      const newEntries = await Promise.all(
        acceptedFiles.map(async (file) => {
          const date = await extractDateFromExif(file); // extract EXIF date

          return {
            id: nanoid(), // unique even when the same file is selected twice
            formId: nanoid(), // stable document ID across retries
            file, // raw file object
            previewUrl: (() => {
              const url = URL.createObjectURL(file);
              previewUrlsRef.current.add(url);
              return url;
            })(), // local preview URL
            scientificName: "", // user input
            commonName: "",
            formLabel: "", // saved under the existing Firestore label key
            lifeformKingdom: "", // manually selected when taxonomy cannot identify kingdom
            isDefault: false, // assigned when the first photo is saved
            date,
            location: "", // display/location name
            locationData: null, // structured map location data
            variant: "", // subspecies/breed/variety
            notes: "", // optional notes attached to this individual photo
            showVariantField: false,
            showNotesField: false,
            speciesExists: false, // flag after Firestore check
            existingForms: [], // existing images for this species
            existingFormsLoaded: {}, // track loading state per thumbnail
            formsToDelete: [], // selected forms for deletion
            uploadStatus: "pending", // pending | uploading | success | error
            uploadCache: null, // Storage URL/path survive a Firestore retry
            collections: [], // assigned collections
            collectionAddedAt: {},
            geminiStatus: "idle",
            geminiTaxonUrl: "",
          };
        }),
      );

      setEntries((prev) => [...prev, ...newEntries]); // append new entries
    },
    [resetSharedDetails],
  );

  function getMissingGeminiContext(entry) {
    const missing = [];

    const hasLocation =
      Boolean(String(entry?.location || "").trim()) ||
      Boolean(entry?.locationData);

    const normalizedDate = normalizeDate(entry?.date);

    const hasDate = [
      normalizedDate.day,
      normalizedDate.month,
      normalizedDate.year,
    ].some((value) => {
      const cleaned = String(value || "").trim();
      return cleaned && cleaned.toUpperCase() !== "NA";
    });

    const hasForm = Boolean(String(entry?.formLabel || "").trim());

    if (!hasLocation) missing.push("location");
    if (!hasDate) missing.push("date");
    if (!hasForm) missing.push("lifeform/life stage");

    return missing;
  }

  function formatMissingGeminiContext(items = []) {
    if (items.length === 0) return "";
    if (items.length === 1) return items[0];
    if (items.length === 2) return `${items[0]} and ${items[1]}`;

    return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
  }

  function suggestWithGemini(index) {
    const entry = entries[index];
    if (!entry?.file) return;

    const missingContext = getMissingGeminiContext(entry);

    if (missingContext.length > 0) {
      setGeminiContextWarning({
        entryId: entry.id,
        missingContext,
      });
      return;
    }

    runGeminiSuggestion(entry.id, []);
  }

  async function runGeminiSuggestion(entryId, missingContext = []) {
    const entry = entries.find((item) => item.id === entryId);
    if (!entry?.file) return;

    setGeminiDialog(null);
    setGeminiContextWarning(null);

    setEntries((prev) =>
      prev.map((e) =>
        e.id === entry.id ? { ...e, geminiStatus: "loading" } : e,
      ),
    );

    try {
      const imageBase64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(entry.file);
      });

      const callable = httpsCallable(functions, "suggestSpeciesFromImage");

      const result = await callable({
        imageBase64,
        mimeType: entry.file.type,
        location: entry.location || "",
        locationData: entry.locationData || null,
        date: entry.date || null,
        formLabel: entry.formLabel || "",
      });

      const suggestion = result.data || {};
      const review = reviewGeminiSuggestion(suggestion);

      setEntries((prev) =>
        prev.map((e) =>
          e.id === entry.id
            ? {
                ...e,
                geminiStatus: review.accepted ? "success" : "error",
              }
            : e,
        ),
      );

      setGeminiDialog({
        entryId: entry.id,
        ...review,
        commonName: String(suggestion.commonName || "").trim(),
        missingContext,
      });
    } catch (error) {
      console.error("Gemini identification failed:", {
        code: error?.code,
        message: error?.message,
        details: error?.details,
        name: error?.name,
        fileName: entry.file?.name,
        mimeType: entry.file?.type,
        fileSizeMB: entry.file?.size
          ? (entry.file.size / 1024 / 1024).toFixed(2)
          : null,
        error,
      });

      const geminiErrorMessage = String(error?.message || "");

      const geminiUnavailable =
        error?.code === "functions/unavailable" ||
        /\b503\b/.test(geminiErrorMessage) ||
        /high demand/i.test(geminiErrorMessage) ||
        /temporarily unavailable/i.test(geminiErrorMessage);

      setEntries((prev) =>
        prev.map((e) =>
          e.id === entry.id ? { ...e, geminiStatus: "error" } : e,
        ),
      );

      setGeminiDialog({
        entryId: entry.id,
        accepted: false,
        name: "",
        rank: "",
        missingContext,
        errorMessage: geminiUnavailable
          ? "Gemini is temporarily busy. Please try again in a moment."
          : "Gemini could not suggest a genus or species for this photo. Try providing more metadata if you have not already (i.e., location, date, lifeform) before attempting to suggest an ID with Gemini again.",
      });
    }
  }

  function acceptGeminiSuggestion() {
    if (!geminiDialog?.accepted) return;
    const { entryId, name, commonName } = geminiDialog;
    setEntries((prev) =>
      prev.map((entry) =>
        entry.id === entryId
          ? {
              ...entry,
              scientificName: name,
              commonName: commonName || entry.commonName,
              speciesExists: false,
              existingForms: [],
              existingFormsLoaded: {},
              formsToDelete: [],
            }
          : entry,
      ),
    );
    setGeminiDialog(null);
    // The existing species-check effect runs after the entry is updated.
  }

  useEffect(() => {
    const dialogOpen =
      geminiDialog || geminiContextWarning || removePhotoDialog;

    if (!dialogOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const closeOnEscape = (event) => {
      if (event.key !== "Escape") return;

      setGeminiDialog(null);
      setGeminiContextWarning(null);
      setRemovePhotoDialog(null);
    };

    document.addEventListener("keydown", closeOnEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [geminiDialog, geminiContextWarning, removePhotoDialog]);

  // Configures drag-and-drop behavior
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [] }, // images only
    multiple: true,
    maxFiles: 10, // enforce upload limit
    onDropRejected: () => {
      setFileLimitError("You can upload a maximum of 10 photos at a time.");
    },
  });

  // Updates a specific field of a specific entry (immutable state update)
  const updateEntry = (index, field, value) => {
    setEntries((prev) =>
      prev.map(
        (entry, i) => (i === index ? { ...entry, [field]: value } : entry), // update only target entry
      ),
    );
  };

  // Requests confirmation before removing a photo from the upload attempt
  const removeEntry = (index) => {
    const entry = entries[index];
    if (!entry) return;

    setRemovePhotoDialog({
      entryId: entry.id,
      photoNumber: index + 1,
    });
  };

  // Removes the confirmed photo from the upload attempt
  const confirmRemoveEntry = () => {
    if (!removePhotoDialog?.entryId) return;

    const entryId = removePhotoDialog.entryId;

    setEntries((prev) => {
      const target = prev.find((entry) => entry.id === entryId);

      if (target?.previewUrl) {
        try {
          URL.revokeObjectURL(target.previewUrl);
        } catch {}
      }

      return prev.filter((entry) => entry.id !== entryId);
    });

    setSuggestions({});
    setActiveSuggestion({});
    setActiveField({});
    setOpenExistingIndex(null);
    setRemovePhotoDialog(null);
  };

  // Toggles whether an existing form is marked for deletion
  function toggleFormDeletion(index, formId) {
    setEntries((prev) =>
      prev.map((e, i) => {
        if (i !== index) return e; // only modify target entry

        const current = e.formsToDelete || [];

        return {
          ...e,
          formsToDelete: current.includes(formId)
            ? current.filter((id) => id !== formId) // remove if already selected
            : [...current, formId], // add if not selected
        };
      }),
    );
  }

  // Updates autocomplete suggestions based on user input (scientific/common name)
  function updateSuggestions(query, index, field) {
    const q = (query || "").toLowerCase().trim(); // normalize query

    //  Only trigger suggestions when 3+ characters
    if (q.length < 3) {
      setSuggestions((prev) => ({
        ...prev,
        [index]: [], // clear suggestions for this entry
      }));

      setActiveField((prev) => ({
        ...prev,
        [index]: field, // track which field is active
      }));

      setActiveSuggestion((prev) => ({
        ...prev,
        [index]: 0, // reset keyboard selection
      }));

      return;
    }

    // Filter species list by scientific or common name match
    const matches = speciesNames.filter(
      (sp) =>
        sp.scientific.toLowerCase().includes(q) ||
        sp.common.toLowerCase().includes(q),
    );

    setSuggestions((prev) => ({
      ...prev,
      [index]: matches, // store matches per entry index
    }));

    setActiveField((prev) => ({
      ...prev,
      [index]: field, // mark active input field
    }));

    setActiveSuggestion((prev) => ({
      ...prev,
      [index]: 0, // default to first suggestion
    }));
  }

  // Applies a selected autocomplete suggestion to an entry
  function selectSuggestion(sp, index) {
    setEntries((prev) =>
      prev.map((e, i) =>
        i === index
          ? {
              ...e,
              scientificName: sp.scientific, // fill scientific name
              commonName: sp.common, // fill common name
              speciesExists: false, // reset existence check
              existingForms: [], // clear previous forms
              existingFormsLoaded: {}, // reset loading state
            }
          : e,
      ),
    );

    setSuggestions((prev) => ({ ...prev, [index]: [] })); // clear suggestions
    setActiveField((prev) => ({ ...prev, [index]: null })); // exit editing state

    /*
     * The species-checking effect below runs after these state updates.
     * Calling checkSpecies here as well would perform the same Firestore read
     * twice.
     */
  }

  // Handles keyboard navigation for autocomplete suggestions
  function handleSuggestionKeyDown(e, index) {
    const list = suggestions[index] || []; // suggestion list for this entry
    const active = activeSuggestion[index] ?? 0; // current highlighted index

    if (!list.length) return; // no suggestions -> do nothing

    if (e.key === "ArrowDown") {
      e.preventDefault(); // prevent cursor movement
      setActiveSuggestion((prev) => ({
        ...prev,
        [index]: Math.min(active + 1, list.length - 1), // move down (bounded)
      }));
    }

    if (e.key === "ArrowUp") {
      e.preventDefault(); // prevent cursor movement
      setActiveSuggestion((prev) => ({
        ...prev,
        [index]: Math.max(active - 1, 0), // move up (bounded)
      }));
    }

    if (e.key === "Enter") {
      e.preventDefault(); // prevent form submission
      selectSuggestion(list[active], index); // select highlighted suggestion
    }
  }

  function createPostUploadCollection() {
    const collectionName = newPostUploadCollection.trim();

    if (!collectionName) {
      setPostUploadCollectionError("Enter a collection name first.");
      return;
    }

    if (collectionName.includes("/") || collectionName.length > 100) {
      setPostUploadCollectionError(
        "Use a collection name of 100 characters or fewer without slashes.",
      );
      return;
    }

    setPostUploadCollectionError("");
    setPostUploadCollectionMessage("");

    const collectionAlreadyExists = collections.includes(collectionName);

    setCollections((prev) =>
      [...new Set([...prev, collectionName])].sort((a, b) =>
        a.localeCompare(b),
      ),
    );

    if (!collectionAlreadyExists) {
      setPendingPostUploadCollections((prev) => [
        ...new Set([...prev, collectionName]),
      ]);
    }

    setPostUploadCollection(collectionName);
    setNewPostUploadCollection("");
    setPostUploadCollectionMessage(
      `\u201C${collectionName}\u201D is ready. Select photos below, then save everything when you finish.`,
    );
  }

  function toggleUploadedPhotoCollection(photo) {
    if (
      !photo?.formId ||
      !postUploadCollection ||
      postUploadCollection === "__new__" ||
      savingPostUploadCollections
    ) {
      return;
    }

    setPostUploadCollectionError("");
    setPostUploadCollectionMessage("");

    setUploadedBatchPhotos((prev) =>
      prev.map((uploadedPhoto) => {
        if (uploadedPhoto.formId !== photo.formId) return uploadedPhoto;

        const currentCollections = Array.isArray(uploadedPhoto.collections)
          ? uploadedPhoto.collections
          : [];
        const alreadyAssigned =
          currentCollections.includes(postUploadCollection);
        const currentCollectionAddedAt = uploadedPhoto.collectionAddedAt || {};
        const nextCollections = alreadyAssigned
          ? currentCollections.filter(
              (collectionName) => collectionName !== postUploadCollection,
            )
          : [...new Set([...currentCollections, postUploadCollection])];
        const nextCollectionAddedAt = alreadyAssigned
          ? Object.fromEntries(
              Object.entries(currentCollectionAddedAt).filter(
                ([collectionName]) => collectionName !== postUploadCollection,
              ),
            )
          : {
              ...currentCollectionAddedAt,
              [postUploadCollection]:
                currentCollectionAddedAt[postUploadCollection] || Date.now(),
            };

        return {
          ...uploadedPhoto,
          collections: nextCollections,
          collectionAddedAt: nextCollectionAddedAt,
          form: {
            ...(uploadedPhoto.form || {}),
            collections: nextCollections,
            collectionAddedAt: nextCollectionAddedAt,
          },
        };
      }),
    );
  }

  async function finishPostUploadReview() {
    if (savingPostUploadCollections || !auth.currentUser) return;

    setSavingPostUploadCollections(true);
    setPostUploadCollectionError("");
    setPostUploadCollectionMessage("");

    try {
      if (
        uploadedBatchPhotos.some(
          (photo) => !photo.speciesDocId || !photo.formId,
        )
      ) {
        throw new Error(
          "One or more uploaded photos are missing the information needed to save their collections.",
        );
      }

      if (emptyPendingPostUploadCollections.length > 0) {
        const emptyCollectionName = emptyPendingPostUploadCollections[0];

        setPostUploadCollection(emptyCollectionName);

        throw new Error(
          `Select at least one photo for \u201C${emptyCollectionName}\u201D before saving. The collection has not been created yet.`,
        );
      }

      const photosBySpecies = uploadedBatchPhotos.reduce((groups, photo) => {
        if (!groups.has(photo.speciesDocId)) {
          groups.set(photo.speciesDocId, []);
        }

        groups.get(photo.speciesDocId).push(photo);
        return groups;
      }, new Map());

      const speciesGroups = [...photosBySpecies.entries()];

      const speciesSnapshots = await Promise.all(
        speciesGroups.map(([speciesDocId]) =>
          getDoc(
            doc(db, "users", auth.currentUser.uid, "species", speciesDocId),
          ),
        ),
      );

      const batch = writeBatch(db);

      speciesGroups.forEach(([speciesDocId, photos], groupIndex) => {
        const speciesSnapshot = speciesSnapshots[groupIndex];

        if (!speciesSnapshot.exists()) {
          throw new Error(
            `The uploaded species record \u201C${speciesDocId}\u201D could not be found.`,
          );
        }

        const pendingPhotosById = new Map(
          photos.map((photo) => [photo.formId, photo]),
        );

        const pendingPhotosByUrl = new Map(
          photos
            .filter((photo) => photo.url)
            .map((photo) => [photo.url, photo]),
        );

        const matchedFormIds = new Set();

        const forms = Array.isArray(speciesSnapshot.data().forms)
          ? speciesSnapshot.data().forms
          : [];

        const selectedDefaultId = pendingPostUploadDefaults[speciesDocId];
        if (
          selectedDefaultId &&
          !forms.some((form) => (form.id || form.url) === selectedDefaultId)
        ) {
          throw new Error(
            `The selected display photo for \u201C${speciesDocId}\u201D is no longer available.`,
          );
        }
        const updatedForms = forms.map((form) => {
          const pendingPhoto =
            pendingPhotosById.get(form.id) || pendingPhotosByUrl.get(form.url);

          if (!pendingPhoto)
            return selectedDefaultId
              ? {
                  ...form,
                  isDefault: (form.id || form.url) === selectedDefaultId,
                }
              : form;

          matchedFormIds.add(pendingPhoto.formId);

          return {
            ...form,
            isDefault: selectedDefaultId
              ? (form.id || form.url) === selectedDefaultId
              : !!form.isDefault,
            collections: Array.isArray(pendingPhoto.collections)
              ? pendingPhoto.collections
              : [],
            collectionAddedAt: pendingPhoto.collectionAddedAt || {},
          };
        });

        if (matchedFormIds.size !== photos.length) {
          throw new Error(
            `One or more uploaded photos for \u201C${speciesDocId}\u201D could not be matched.`,
          );
        }

        batch.update(speciesSnapshot.ref, {
          forms: selectedDefaultId
            ? sortDefaultFirst(updatedForms)
            : updatedForms,
        });
      });

      pendingPostUploadCollections.forEach((collectionName) => {
        batch.set(
          doc(
            db,
            "users",
            auth.currentUser.uid,
            "collectionsMeta",
            collectionName,
          ),
          {
            title: collectionName,
            description: "",
          },
          { merge: true },
        );
      });

      batch.set(
        doc(db, "profile", auth.currentUser.uid),
        {
          sectionUpdatedAt: {
            collections: Date.now(),
            taxonomy: Date.now(),
            profile: Date.now(),
          },
        },
        { merge: true },
      );

      await batch.commit();

      setUploadSuccess(false);
      setUploadedBatchPhotos([]);
      setUploadedBatchSpecies([]);
      setPostUploadDefaultOptions({});
      setPendingPostUploadDefaults({});
      setPostUploadCommunitySummary([]);
      setPostUploadCommunityLoading(false);
      setPostUploadCommunityError("");
      setPostUploadFriendCount(0);
      setNewTaxonomyUnlocks([]);
      setTaxonomyUnlockDetailsOpen(false);
      setExpandedFriendComparisons({});
      setPostUploadCarouselIndexes({});
      setPostUploadCollectionToolsOpen(false);
      setPostUploadCollection("");
      setNewPostUploadCollection("");
      setPendingPostUploadCollections([]);
      setPostUploadCollectionError("");
      setPostUploadCollectionMessage("");
      resetSharedDetails();
    } catch (error) {
      console.error("Failed to save collection changes:", error);

      setPostUploadCollectionError(
        error?.message ||
          "Your collection changes could not be saved. Nothing has been cleared, so you can try again.",
      );
    } finally {
      setSavingPostUploadCollections(false);
    }
  }

  function updateGlobalSpeciesSuggestions(value) {
    const q = (value || "").toLowerCase().trim();

    setGlobalSpeciesQuery(value);

    if (q.length < 3) {
      setGlobalSpeciesSuggestions([]);
      return;
    }

    setGlobalSpeciesSuggestions(
      speciesNames.filter(
        (sp) =>
          sp.scientific.toLowerCase().includes(q) ||
          sp.common.toLowerCase().includes(q),
      ),
    );
  }

  function beginSharedDetails() {
    setSharedField("species");
    setSharedStep(1);
    setSharedSelectedIds(
      entries
        .filter((entry) => entry.uploadStatus !== "success")
        .map((entry) => entry.id),
    );
    setSharedModalError("");
    setSharedAppliedMessage("");
    setGlobalSpeciesQuery("");
    setGlobalCommonName("");
    setGlobalSpeciesSuggestions([]);
    setGlobalDate({ day: "NA", month: "NA", year: "NA" });
    setGlobalLocation("");
    setGlobalLocationData(null);
    setSharedDetailsOpen(true);
  }

  function applySelectedSharedDetails() {
    const selected = new Set(sharedSelectedIds);
    if (!selected.size) {
      setSharedModalError("Select at least one photo.");
      setSharedStep(2);
      return;
    }

    if (sharedField === "species" && !globalSpeciesQuery.trim()) {
      setSharedModalError("Enter a scientific name first.");
      return;
    }
    if (
      sharedField === "date" &&
      [globalDate.day, globalDate.month, globalDate.year].every(
        (part) => part === "NA",
      )
    ) {
      setSharedModalError("Choose at least one date component.");
      return;
    }
    if (sharedField === "location" && !globalLocationData) {
      setSharedModalError("Select a location on the map first.");
      return;
    }

    const scientificName = globalSpeciesQuery.trim();
    const commonName = globalCommonName.trim();
    const date = normalizeDate(globalDate);

    setEntries((previous) =>
      previous.map((entry) => {
        if (!selected.has(entry.id) || entry.uploadStatus === "success")
          return entry;
        if (sharedField === "species") {
          return {
            ...entry,
            scientificName,
            commonName,
            speciesExists: false,
            existingForms: [],
            existingFormsLoaded: {},
          };
        }
        if (sharedField === "date") return { ...entry, date: { ...date } };
        return {
          ...entry,
          location: globalLocationData.name || globalLocation,
          locationData: globalLocationData,
        };
      }),
    );

    const fieldName =
      sharedField === "species"
        ? "Scientific name"
        : sharedField === "date"
          ? "Date"
          : "Location";
    setGlobalSpeciesSuggestions([]);
    setSharedModalError("");
    setSharedAppliedMessage(
      `${fieldName} applied to ${selected.size} photo${selected.size === 1 ? "" : "s"}.`,
    );
    setSharedStep(4);
  }

  // Keep the current upload batch in place when adding another shared field.
  function addAnotherSharedDetail() {
    setSharedStep(1);
    setSharedField("species");
    setSharedModalError("");
    setSharedAppliedMessage("");
    setGlobalSpeciesQuery("");
    setGlobalCommonName("");
    setGlobalSpeciesSuggestions([]);
    setGlobalDate({ day: "NA", month: "NA", year: "NA" });
    setGlobalLocation("");
    setGlobalLocationData(null);
  }

  // Checks if a species already exists in Firestore and loads its forms
  const checkSpecies = async (sciName, index) => {
    const sci = (sciName || "").trim();

    if (!sci || !auth.currentUser) return;

    const docId = normalizeSpeciesDocId(sci);

    const speciesRef = doc(db, "users", auth.currentUser.uid, "species", docId);

    try {
      const snap = await getDoc(speciesRef);

      if (snap.exists()) {
        const data = snap.data();

        const genus = sci.split(/\s+/)[0];
        const family = getFamilyFromGenus(genus);

        setEntries((prev) =>
          prev.map((entry, i) =>
            i === index
              ? {
                  ...entry,
                  speciesExists: true,
                  family,
                  existingForms: Array.isArray(data.forms)
                    ? sortDefaultFirst(data.forms)
                    : [],
                }
              : entry,
          ),
        );

        return;
      }

      setEntries((prev) =>
        prev.map((entry, i) =>
          i === index
            ? {
                ...entry,
                speciesExists: false,
                existingForms: [],
              }
            : entry,
        ),
      );
    } catch (error) {
      const message = String(error?.message || "").toLowerCase();

      const connectionUnavailable =
        error?.code === "unavailable" ||
        message.includes("client is offline") ||
        message.includes("network");

      if (connectionUnavailable) {
        console.warn(`Species lookup temporarily unavailable for ${sci}.`);

        return;
      }

      console.error(`Failed to check whether ${sci} already exists:`, error);
    }
  };

  // Handles full upload pipeline: validation -> storage -> Firestore update
  async function handleUpload() {
    // Prevent overlapping attempts if the button is tapped repeatedly.
    if (activeUploadControlRef.current) return;
    const firstMissingName = entries.find(
      (entry) =>
        entry.uploadStatus !== "success" &&
        !String(entry.scientificName || "").trim(),
    );
    if (firstMissingName) {
      // The first missing field may belong to a different, currently hidden
      // photo. Select that photo before scrolling to the actual input.
      setActivePhotoIndex(entries.indexOf(firstMissingName));
      setUploadView("editing");
      setUploadError(null);
      setHighlightMissingNames(true);
      setShowMissingNameAlert(true);
      window.requestAnimationFrame(() => {
        document
          .getElementById(`scientific-name-${firstMissingName.id}`)
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
      return;
    }
    setShowMissingNameAlert(false);
    setHighlightMissingNames(false);
    document.activeElement?.blur();
    // No UI transition until authentication and name validation pass.
    if (!auth.currentUser) {
      setUploadError("You must be logged in to upload.");
      return;
    }
    const resumeIndex = entries.findIndex(
      (entry) => entry.uploadStatus !== "success",
    );
    // The first photo's stable form ID identifies this entire batch across
    // retries. No new ID is generated when a partially saved batch resumes.
    const uploadBatchId = entries[0]?.formId;
    const uploadControl = { cancelled: false, cancelCurrent: null };
    activeUploadControlRef.current = uploadControl;
    setProgressPhotoIndex(Math.max(0, resumeIndex));
    setUploadView("progress");
    setIsUploading(true);
    setUploadError(null);
    setUploadSuccess(false);

    try {
      let changedTaxonomy = false;
      let changedCollections = false;
      let hadFailures = false;
      let failureDetails = "";
      const successfulPhotos = [];

      // Process each entry sequentially
      for (let i = 0; i < entries.length; i++) {
        const entry = entries[i];

        // Never upload an entry twice after a partially successful batch.
        if (entry.uploadStatus === "success") {
          continue;
        }

        try {
          if (uploadControl.cancelled) throw stoppedUploadError();
          if (typeof navigator !== "undefined" && !navigator.onLine) {
            throw new Error("Your device is offline. Reconnect to the internet and retry.");
          }
          setProgressPhotoIndex(i);
          setUploadPercents((previous) => ({ ...previous, [entry.id]: 0 }));
          // mark entry as uploading
          setEntries((prev) =>
            prev.map((e, idx) =>
              idx === i ? { ...e, uploadStatus: "uploading" } : e,
            ),
          );

          const sci = (entry.scientificName || "").trim();
          if (!sci) {
            hadFailures = true;
            failureDetails = `Photo ${i + 1} is missing its scientific name.`;

            setEntries((prev) =>
              prev.map((e, idx) =>
                idx === i ? { ...e, uploadStatus: "error" } : e,
              ),
            );

            break; // resume at this photo; leave all later photos pending
          }

          const docId = normalizeSpeciesDocId(sci); // consistent doc ID
          const speciesRef = doc(
            db,
            "users",
            auth.currentUser.uid,
            "species",
            docId,
          );

          const snap = await awaitUploadStep(
            () => getDoc(speciesRef),
            "Checking the species record",
            UPLOAD_READ_TIMEOUT_MS,
            uploadControl,
          );
          const speciesWasNew = !snap.exists();
          const existingSpeciesData = speciesWasNew ? {} : snap.data();

          const genus = sci.split(/\s+/)[0] || "UnknownGenus";
          const safeOriginalName = entry.file.name.replace(/[^\w.\- ()]/g, "_");
          // A successful Storage upload is reused when only the Firestore write
          // fails. A stable path prevents orphaned duplicate Storage objects.
          const objectPath =
            entry.uploadCache?.objectPath ||
            `species/${genus}/${entry.formId}_${safeOriginalName}`;
          const storageRef = ref(storage, objectPath);
          let downloadURL = entry.uploadCache?.downloadURL;

          if (!downloadURL) {
            const task = uploadBytesResumable(storageRef, entry.file, {
              contentType: entry.file.type,
              customMetadata: { ownerUid: auth.currentUser.uid },
            });
            await awaitStorageTransfer(task, (snapshot) => {
              const percent = snapshot.totalBytes
                ? Math.round((100 * snapshot.bytesTransferred) / snapshot.totalBytes)
                : 0;
              setUploadPercents((prev) => ({ ...prev, [entry.id]: percent }));
            }, uploadControl);
            downloadURL = await awaitUploadStep(
              () => getDownloadURL(storageRef),
              "Retrieving the uploaded photo",
              UPLOAD_READ_TIMEOUT_MS,
              uploadControl,
            );
            setEntries((prev) =>
              prev.map((item) =>
                item.id === entry.id
                  ? { ...item, uploadCache: { objectPath, downloadURL } }
                  : item,
              ),
            );
          } else {
            setUploadPercents((prev) => ({ ...prev, [entry.id]: 100 }));
          }

          const { label } = normalizeLabel(entry.formLabel);

          // construct new form object
          const newForm = {
            id: entry.formId, // stable even if a Firestore write is retried
            label,
            lifeformKingdom: entry.lifeformKingdom || "",
            url: downloadURL,
            storagePath: objectPath,
            date: normalizeDate(entry.date),
            location: (entry.location || "").trim(),
            locationName: (entry.location || "").trim(),
            locationLat: entry.locationData?.lat ?? null,
            locationLng: entry.locationData?.lng ?? null,
            locationCountry: entry.locationData?.country || "",
            locationAdminArea: entry.locationData?.adminArea || "",
            locationCity: entry.locationData?.city || "",
            locationPlaceId: entry.locationData?.placeId || "",
            variant: (entry.variant || "NA").trim(),
            notes: (entry.notes || "").trim(),
            notesUpdatedAt: entry.notes?.trim() ? new Date() : null,
            collections: entry.collections || [],
            collectionAddedAt: Object.fromEntries(
              (entry.collections || []).map((collectionName) => [
                collectionName,
                entry.collectionAddedAt?.[collectionName] || Date.now(),
              ]),
            ),
            isDefault: false,
            createdAt: new Date(),
            // Publication is deferred until every photo in the batch is saved.
            uploadBatchId,
            uploadBatchAnnouncementEligible: speciesWasNew,
            uploadBatchNeedsReconciliation: false,
          };

          const formAlreadySaved =
            !speciesWasNew &&
            (existingSpeciesData.forms || []).some(
              (form) => form.id === newForm.id,
            );
          if (formAlreadySaved) {
            // A previous write may have committed despite a network error.
            // Count it as complete without appending the same form twice.
            changedTaxonomy = true;
            changedCollections =
              changedCollections || (entry.collections || []).length > 0;
            setPostUploadDefaultOptions((previous) => ({
              ...previous,
              [docId]: sortDefaultFirst(existingSpeciesData.forms || [])
                .filter((form) => form?.url)
                .map((form) => ({
                  id: form.id || form.url,
                  url: form.url,
                  isDefault: !!form.isDefault,
                })),
            }));
            setEntries((prev) =>
              prev.map((item) =>
                item.id === entry.id
                  ? { ...item, uploadStatus: "success" }
                  : item,
              ),
            );
          } else if (speciesWasNew) {
            // create new species document
            const firstForm = { ...newForm, isDefault: true }; // enforce default

            await awaitUploadStep(
              () => setDoc(speciesRef, {
                genus,
                name: sci,
                common: (entry.commonName || "").trim(),
                owner: auth.currentUser.uid,
                createdAt: new Date(),
                forms: [firstForm],
              }),
              "Saving the species record",
              UPLOAD_WRITE_TIMEOUT_MS,
              uploadControl,
            );

            changedTaxonomy = true;
            changedCollections =
              changedCollections || (firstForm.collections || []).length > 0;

            // mark success
            setEntries((prev) =>
              prev.map((e, idx) =>
                idx === i ? { ...e, uploadStatus: "success" } : e,
              ),
            );
          } else {
            const data = existingSpeciesData;
            const existingForms = Array.isArray(data.forms)
              ? [...data.forms]
              : [];
            const formsToDelete = entry.formsToDelete || [];

            let updatedForms = existingForms;

            // replace mode: overwrite selected form
            if (
              mode === "replace" &&
              replaceIndex !== null &&
              replaceIndex >= 0
            ) {
              const uiForms = sortDefaultFirst(existingForms); // UI order
              const target = uiForms[replaceIndex];

              if (target?.id) {
                updatedForms = existingForms.map((f) =>
                  f.id === target.id ? newForm : f,
                );
              } else {
                updatedForms = [...existingForms];
                updatedForms[0] = newForm;
              }
            } else {
              // normal mode: remove selected forms, then append new one
              const filteredForms = existingForms.filter(
                (f) => !formsToDelete.includes(f.id),
              );

              updatedForms = [...filteredForms, newForm];
            }

            const hasExistingDefault = updatedForms.some((f) => f?.isDefault);

            // Keep the prior default until the user chooses a new one on
            // Upload complete. A deleted default falls back to the first form.
            if (!hasExistingDefault && updatedForms.length > 0) {
              updatedForms = updatedForms.map((f, idx) => ({
                ...f,
                isDefault: idx === 0,
              }));
            }

            // A replacement or an upload accompanied by a deletion does not
            // increase the saved photo count, so it is not a new-photo alert.
            newForm.uploadBatchAnnouncementEligible =
              updatedForms.length > existingForms.length;
            newForm.uploadBatchNeedsReconciliation =
              mode === "replace" || formsToDelete.length > 0;
            updatedForms = sortDefaultFirst(updatedForms); // keep default first
            // update species document
            await awaitUploadStep(
              () => updateDoc(speciesRef, {
                name: sci,
                common: (entry.commonName || data.common || "").trim(),
                owner: auth.currentUser.uid,
                forms: updatedForms,
              }),
              "Updating the species record",
              UPLOAD_WRITE_TIMEOUT_MS,
              uploadControl,
            );
            setPostUploadDefaultOptions((previous) => ({
              ...previous,
              [docId]: updatedForms
                .filter((form) => form?.url)
                .map((form) => ({
                  id: form.id || form.url,
                  url: form.url,
                  isDefault: !!form.isDefault,
                })),
            }));

            changedTaxonomy = true;
            changedCollections =
              changedCollections ||
              (entry.collections || []).length > 0 ||
              (entry.formsToDelete || []).length > 0;

            // mark success
            setEntries((prev) =>
              prev.map((e, idx) =>
                idx === i ? { ...e, uploadStatus: "success" } : e,
              ),
            );
          }

          successfulPhotos.push({
            formId: newForm.id,
            speciesDocId: docId,
            scientificName: sci,
            commonName: (
              entry.commonName ||
              existingSpeciesData.common ||
              ""
            ).trim(),
            formLabel: newForm.label,
            url: newForm.url,
            form: {
              id: newForm.id,
              label: newForm.label,
              date: newForm.date,
              location: newForm.location,
              locationName: newForm.locationName,
              locationLat: newForm.locationLat,
              locationLng: newForm.locationLng,
              locationCountry: newForm.locationCountry,
              locationAdminArea: newForm.locationAdminArea,
              locationCity: newForm.locationCity,
              locationPlaceId: newForm.locationPlaceId,
              variant: newForm.variant,
              notes: newForm.notes,
              collections: newForm.collections,
            },
            wasNewToUser: speciesWasNew,
            collections: newForm.collections,
            collectionAddedAt: newForm.collectionAddedAt,
          });
        } catch (entryError) {
          console.error(
            `Upload failed for ${entry.file?.name || `photo ${i + 1}`}:`,
            entryError,
          );

          hadFailures = true;
          failureDetails = `Photo ${i + 1}: ${entryError?.message || "An unexpected upload error occurred."}`;

          setEntries((prev) =>
            prev.map((e, idx) =>
              idx === i ? { ...e, uploadStatus: "error" } : e,
            ),
          );
          setProgressPhotoIndex(i);
          // Do not advance past the failed photo. Retrying continues here.
          break;
        }
      }

      if (successfulPhotos.length > 0) {
        try {
          const unlockedTaxa = await awaitUploadStep(
            () => findNewTaxonomyUnlocks(auth.currentUser.uid, successfulPhotos),
            "Updating your taxonomy summary",
            UPLOAD_READ_TIMEOUT_MS,
            uploadControl,
          );

          setNewTaxonomyUnlocks((previousUnlocks) => {
            const unlocksByKey = new Map(
              previousUnlocks.map((unlock) => [
                `${unlock.rank}:${unlock.taxonName}`,
                unlock,
              ]),
            );

            unlockedTaxa.forEach((unlock) => {
              unlocksByKey.set(`${unlock.rank}:${unlock.taxonName}`, unlock);
            });

            return Array.from(unlocksByKey.values());
          });
        } catch (taxonomySummaryError) {
          console.error(
            "Unable to calculate newly unlocked taxonomy:",
            taxonomySummaryError,
          );
        }

        setUploadedBatchPhotos((prev) => {
          const existingFormIds = new Set(prev.map((photo) => photo.formId));

          return [
            ...prev,
            ...successfulPhotos.filter(
              (photo) => !existingFormIds.has(photo.formId),
            ),
          ];
        });

        setUploadedBatchSpecies((prev) => {
          const speciesById = new Map(
            prev.map((species) => [species.speciesDocId, species]),
          );

          successfulPhotos.forEach((photo) => {
            const existingSpecies = speciesById.get(photo.speciesDocId);

            speciesById.set(photo.speciesDocId, {
              speciesDocId: photo.speciesDocId,
              scientificName:
                existingSpecies?.scientificName || photo.scientificName,
              commonName: existingSpecies?.commonName || photo.commonName || "",
              wasNewToUser:
                !!existingSpecies?.wasNewToUser || !!photo.wasNewToUser,
            });
          });

          return Array.from(speciesById.values());
        });
      }

      if (changedTaxonomy || changedCollections) {
        try {
          await awaitUploadStep(
            () => bumpProfileCacheMarkers(auth.currentUser.uid, {
              taxonomy: changedTaxonomy,
              collections: changedCollections,
              quiz: changedTaxonomy,
            }),
            "Refreshing your profile summary",
            UPLOAD_READ_TIMEOUT_MS,
            uploadControl,
          );
        } catch (cacheError) {
          // The authoritative uploads succeeded. A cache-marker problem
          // must not cause those photos to be uploaded a second time.
          console.error("Unable to refresh profile cache markers:", cacheError);
        }
      }

      if (hadFailures) {
        // Keep ALL entries for the progress carousel. Successful records are
        // skipped by handleUpload when the user retries from the failed photo.
        setUploadError(
          `${failureDetails || "The upload was interrupted."} Earlier completed photos are saved, and the remaining details are preserved. Retry to continue from that photo.`,
        );
        return;
      }

      // Each species/form is already saved. Notify friends only once the
      // entire batch has succeeded; retrying this step never reuploads photos.
      // The callable validates every form on the server before publication.
      const finalizeBatch = httpsCallable(
        functions,
        "finalizeBioIndexPhotoUploadBatch",
        { timeout: 120000 },
      );
      const finalization = await awaitUploadStep(
        () => finalizeBatch({
          batchId: uploadBatchId,
          expectedFormIds: entries.map((entry) => entry.formId),
        }),
        "Publishing your completed photo batch",
        UPLOAD_WRITE_TIMEOUT_MS,
        uploadControl,
      );
      if (!finalization.data?.finalized) {
        throw new Error(
          "Photos were saved, but the batch announcement could not be completed. Retry Upload to finish.",
        );
      }

      // Reset only after the entire batch and its publication succeed.
      setEntries([]);
      setUploadView("editing");
      setUploadPercents({});
      setMode("new");
      setReplaceIndex(null);
      setSuggestions({});
      setActiveSuggestion({});
      setActiveField({});
      setOpenExistingIndex(null);
      setTaxonomyUnlockDetailsOpen(false);
      setExpandedFriendComparisons({});
      setPostUploadCarouselIndexes({});
      setPostUploadCollectionToolsOpen(false);
      resetSharedDetails();
      setUploadSuccess(true);
    } catch (err) {
      console.error(err);

      // mark any in-progress uploads as failed
      setEntries((prev) =>
        prev.map((e) =>
          e.uploadStatus === "uploading" ? { ...e, uploadStatus: "error" } : e,
        ),
      );

      setUploadError(
        `${err?.message || "The upload stopped unexpectedly."} Completed photos remain saved, and your remaining details are preserved. Select Retry Upload to continue.`,
      );
    } finally {
      if (activeUploadControlRef.current === uploadControl) {
        activeUploadControlRef.current = null;
      }
      setIsUploading(false); // always release lock
    }
  }

  // Clears active input focus state for a specific entry (exits editing mode)
  function clearNameEditing(index) {
    setActiveField((prev) => ({ ...prev, [index]: null })); // unset active field
  }

  // ===================== Effects =====================

  // Revoke blob URLs only when their photo actually leaves the batch.
  // In particular, uploading/success/error state updates MUST NOT revoke
  // their previews or the progress carousel will display broken images.
  useEffect(() => {
    const activeUrls = new Set(entries.map((entry) => entry.previewUrl));
    for (const url of previewUrlsRef.current) {
      if (!activeUrls.has(url)) {
        URL.revokeObjectURL(url);
        previewUrlsRef.current.delete(url);
      }
    }
  }, [entries]);

  useEffect(() => {
    const previews = previewUrlsRef.current;
    return () => {
      for (const url of previews) URL.revokeObjectURL(url);
      previews.clear();
    };
  }, []);

  useEffect(() => {
    if (uploadView !== "progress") return;
    const carousel = progressCarouselRef.current;
    const item = carousel?.querySelector('[data-progress-photo-active="true"]');
    if (!carousel || !item) return;
    const track = carousel.getBoundingClientRect();
    const bounds = item.getBoundingClientRect();
    if (bounds.left < track.left + 8) {
      carousel.scrollBy({
        left: bounds.left - track.left - 8,
        behavior: "smooth",
      });
    } else if (bounds.right > track.right - 8) {
      carousel.scrollBy({
        left: bounds.right - track.right + 8,
        behavior: "smooth",
      });
    }
  }, [progressPhotoIndex, uploadView]);

  useLayoutEffect(() => {
    if (uploadView === "progress" && !uploadSuccess) {
      const target = document.getElementById("bioindex-upload-progress");
      target?.focus({ preventScroll: true });
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    } else if (uploadView === "editing" && entries.length > 0) {
      // Returning from a failed upload to edit the first remaining photo.
      window.requestAnimationFrame(() => {
        uploadEditorRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
    }
    // Only scroll on a view change, not on every individual progress update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadView]);

  // Load user's collections once on mount
  useEffect(() => {
    async function loadCollections() {
      if (!auth.currentUser) return;

      try {
        const colSnap = await getDocs(
          collection(db, "users", auth.currentUser.uid, "collectionsMeta"),
        );

        const names = colSnap.docs.map(
          (documentSnapshot) => documentSnapshot.id,
        );

        setCollections(names);
      } catch (error) {
        const message = String(error?.message || "").toLowerCase();

        const connectionUnavailable =
          error?.code === "unavailable" ||
          message.includes("client is offline") ||
          message.includes("network");

        if (connectionUnavailable) {
          console.warn(
            "Collections could not be loaded because Firestore is temporarily offline.",
          );

          setCollections([]);
          return;
        }

        console.error("Failed to load collections:", error);
      }
    }

    loadCollections();
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (
      !uploadSuccess ||
      !auth.currentUser ||
      uploadedBatchSpecies.length === 0
    ) {
      setPostUploadCommunitySummary([]);
      setPostUploadCommunityLoading(false);
      setPostUploadCommunityError("");
      setPostUploadFriendCount(0);
      return undefined;
    }

    const summaryWithoutMatches = uploadedBatchSpecies.map((species) => ({
      ...species,
      friendMatches: [],
    }));

    setPostUploadCommunitySummary(summaryWithoutMatches);
    setPostUploadCommunityLoading(true);
    setPostUploadCommunityError("");

    async function loadPostUploadCommunitySummary() {
      try {
        const currentUid = auth.currentUser?.uid;

        if (!currentUid) return;

        const friendsSnapshot = await getDocs(
          collection(db, "users", currentUid, "friends"),
        );

        const friendUids = friendsSnapshot.docs.map(
          (friendDocument) => friendDocument.id,
        );

        if (cancelled) return;

        setPostUploadFriendCount(friendUids.length);

        if (friendUids.length === 0) {
          setPostUploadCommunityLoading(false);
          return;
        }

        const profileSnapshots = await Promise.all(
          friendUids.map((friendUid) => getDoc(doc(db, "profile", friendUid))),
        );

        const friendProfiles = new Map(
          friendUids.map((friendUid, index) => [
            friendUid,
            profileSnapshots[index].exists()
              ? profileSnapshots[index].data()
              : {},
          ]),
        );

        const friendSpeciesMatches = await Promise.all(
          friendUids.flatMap((friendUid) =>
            uploadedBatchSpecies.map(async (uploadedSpecies) => {
              const speciesSnapshot = await getDoc(
                doc(
                  db,
                  "users",
                  friendUid,
                  "species",
                  uploadedSpecies.speciesDocId,
                ),
              );

              if (!speciesSnapshot.exists()) return null;

              const speciesData = speciesSnapshot.data();
              const forms = Array.isArray(speciesData.forms)
                ? speciesData.forms.filter((form) => form?.url)
                : [];

              if (forms.length === 0) return null;

              const friendProfile = friendProfiles.get(friendUid) || {};
              const friendName =
                String(friendProfile.name || "BioIndex friend").trim() ||
                "BioIndex friend";

              return {
                speciesDocId: uploadedSpecies.speciesDocId,
                friendUid,
                friendName,
                friendPhotoURL: String(friendProfile.photoURL || "").trim(),
                scientificName:
                  speciesData.name || uploadedSpecies.scientificName,
                commonName:
                  speciesData.common || uploadedSpecies.commonName || "",
                forms: forms.map((form) => ({
                  ...form,
                  ownerUID: friendUid,
                  ownerName: friendName,
                })),
              };
            }),
          ),
        );

        if (cancelled) return;

        const matchesBySpeciesId = new Map();

        friendSpeciesMatches.filter(Boolean).forEach((friendMatch) => {
          const existingMatches =
            matchesBySpeciesId.get(friendMatch.speciesDocId) || [];

          existingMatches.push(friendMatch);
          matchesBySpeciesId.set(friendMatch.speciesDocId, existingMatches);
        });

        setPostUploadCommunitySummary(
          uploadedBatchSpecies.map((species) => ({
            ...species,
            friendMatches: matchesBySpeciesId.get(species.speciesDocId) || [],
          })),
        );
      } catch (error) {
        console.error(
          "Failed to compare uploaded species with friends:",
          error,
        );

        if (!cancelled) {
          setPostUploadCommunityError(
            "Friend comparison is temporarily unavailable.",
          );
        }
      } finally {
        if (!cancelled) setPostUploadCommunityLoading(false);
      }
    }

    loadPostUploadCommunitySummary();

    return () => {
      cancelled = true;
    };
  }, [uploadSuccess, uploadedBatchSpecies]);

  useLayoutEffect(() => {
    if (!uploadSuccess) return undefined;

    function forceUploadSummaryToTop() {
      const summary = document.getElementById("upload-success-summary");
      const scrollingElement =
        document.scrollingElement || document.documentElement;

      summary?.focus({ preventScroll: true });
      scrollingElement.scrollTop = 0;
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }

    forceUploadSummaryToTop();

    const animationFrame = window.requestAnimationFrame(
      forceUploadSummaryToTop,
    );
    const layoutTimer = window.setTimeout(forceUploadSummaryToTop, 500);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.clearTimeout(layoutTimer);
    };
  }, [uploadSuccess, isUploading]);

  // Auto-check species existence when user stops editing and no suggestions are open
  useEffect(() => {
    // Upload-status updates must not repeatedly trigger species lookups.
    if (uploadView === "progress") return;
    entries.forEach((entry, index) => {
      const notEditing =
        activeField[index] !== "scientific" && activeField[index] !== "common"; // skip while typing

      const hasScientificName = (entry.scientificName || "").trim().length > 0; // require input
      const hasSuggestionsOpen = (suggestions[index] || []).length > 0; // avoid conflict with dropdown

      if (
        notEditing &&
        hasScientificName &&
        !hasSuggestionsOpen &&
        entry.existingForms.length === 0 && // skip if already loaded
        !entry.speciesExists // skip if already confirmed
      ) {
        checkSpecies(entry.scientificName, index); // query Firestore
      }
    });
  }, [entries, activeField, suggestions, uploadView]);

  const uploadedSpeciesCount = uploadedBatchSpecies.length;
  const newUploadedSpeciesCount = uploadedBatchSpecies.filter(
    (species) => species.wasNewToUser,
  ).length;
  const speciesSharedWithFriendsCount = postUploadCommunitySummary.filter(
    (species) => (species.friendMatches || []).length > 0,
  ).length;
  const newGenusUnlocks = newTaxonomyUnlocks
    .filter((unlock) => unlock.rank === "genus")
    .map((genusUnlock) => ({
      ...genusUnlock,
      broaderUnlocks: newTaxonomyUnlocks
        .filter(
          (unlock) =>
            unlock.speciesDocId === genusUnlock.speciesDocId &&
            unlock.rank !== "genus",
        )
        .sort(
          (a, b) =>
            ["family", "order", "class", "phylum", "kingdom"].indexOf(a.rank) -
            ["family", "order", "class", "phylum", "kingdom"].indexOf(b.rank),
        ),
    }));

  return (
    <div
      className="bioindex-upload-root min-h-screen bg-[#374049] px-3 pb-8 sm:px-8"
      style={{ overflowAnchor: "none" }}
    >
      {/* Desktop navigation uses the same 1,360px shell as Profile and Community. */}
      <div className="bioindex-v7-desktop-nav relative z-40 mx-auto w-full max-w-[1360px] pt-3 pb-0">
        <div className="flex w-full items-center gap-3 rounded-[1.6rem] border border-[#93CA87]/35 bg-white px-3 py-3 shadow-[0_16px_40px_rgba(15,36,40,0.18),inset_0_1px_0_rgba(255,255,255,0.72)] sm:px-4 lg:px-6">
          <Link
            to="/"
            aria-label="BioIndex Home"
            className="hidden shrink-0 items-center gap-3 rounded-2xl px-2.5 py-2 text-[#374049] transition hover:bg-[#93CA87]/10 lg:flex"
          >
            <img
              src="/favicon.ico"
              alt=""
              className="h-11 w-11 shrink-0 object-contain drop-shadow-[0_4px_10px_rgba(1,118,122,0.16)]"
            />
            <span className="flex flex-col leading-none">
              <span
                className="text-[29px] text-[#374049]"
                style={{ fontFamily: "Cormorant Garamond" }}
              >
                BioIndex
              </span>
              <span className="mt-1 text-[10px] font-semibold uppercase tracking-[0.28em] text-[#01767A]/70">
                Add Photos
              </span>
            </span>
          </Link>
          <span
            className="hidden h-7 w-px bg-[#374049]/10 lg:block"
            aria-hidden="true"
          />
          <nav
            aria-label="BioIndex navigation"
            className="grid min-w-0 flex-1 grid-cols-6 items-center gap-1 rounded-2xl bg-[#374049]/[0.03] p-1.5 sm:flex sm:justify-end sm:bg-transparent sm:p-0"
          >
            <Link to="/" aria-label="Home" className="bioindex-upload-nav-link">
              Home
            </Link>
            <Link
              to="/explore"
              aria-label="Explore Taxonomy"
              className="bioindex-upload-nav-link"
            >
              Explore Taxonomy
            </Link>
            <div
              aria-label="Add Photos"
              aria-current="page"
              className="bioindex-upload-nav-current"
            >
              Add Photos
            </div>
            <Link
              to="/profile"
              aria-label="Profile"
              className="bioindex-upload-nav-link"
            >
              Profile
            </Link>
            <Link
              to="/forum"
              aria-label="Community"
              className="bioindex-upload-nav-link relative"
            >
              Community
              <CommunityNavBadge />
            </Link>
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              aria-label="Search"
              className="bioindex-upload-nav-link"
            >
              Search
            </button>
          </nav>
        </div>
      </div>

      {/* page wrapper */}
      <div className="bioindex-upload-content mx-auto w-full max-w-[1360px]">
        {/* centered container */}
        <div className="bioindex-upload-shell rounded-3xl border border-[#93CA87]/40 bg-white p-3 shadow-xl sm:p-8 lg:p-10">
          {!uploadSuccess && uploadView === "editing" && (
            <header className="bioindex-upload-intro">
              <h1>Upload Photos to Your Collection</h1>
              <div
                className="bioindex-upload-collection-totals"
                aria-live="polite"
              >
                {collectionTotals ? (
                  <>
                    <span>
                      <strong>
                        {collectionTotals.photos.toLocaleString()}
                      </strong>{" "}
                      photo{collectionTotals.photos === 1 ? "" : "s"}
                    </span>
                    <span
                      aria-hidden="true"
                      className="bioindex-upload-totals-dot"
                    >
                      •
                    </span>
                    <span>
                      <strong>
                        {collectionTotals.species.toLocaleString()}
                      </strong>{" "}
                      species
                    </span>
                  </>
                ) : (
                  <span>
                    {collectionTotalsError
                      ? "Collection totals unavailable"
                      : "Loading collection totals..."}
                  </span>
                )}
              </div>
            </header>
          )}
          {uploadSuccess && (
            <div
              id="upload-success-summary"
              tabIndex={-1}
              className="bioindex-upload-success space-y-6 outline-none"
            >
              <header className="px-2 py-2 text-center">
                <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-[#24C3BE] text-lg font-bold text-white">
                  &#x2713;
                </div>
                <h1
                  className="mt-3 text-4xl font-semibold leading-none text-[#374049] sm:text-5xl"
                  style={{ fontFamily: "Cormorant Garamond" }}
                >
                  Upload complete
                </h1>
                <p className="mx-auto mt-2 max-w-2xl text-sm leading-6 text-[#374049]/60">
                  Your photos are safely in BioIndex.
                </p>
              </header>

              <section className="overflow-hidden rounded-2xl border border-[#93CA87]/45 bg-white shadow-sm">
                <div className="bg-white px-5 py-5 sm:px-6">
                  <p className="text-sm leading-6 text-[#374049]/70">
                    You uploaded{" "}
                    <strong className="font-semibold text-[#374049]">
                      {uploadedBatchPhotos.length.toLocaleString()}{" "}
                      {uploadedBatchPhotos.length === 1 ? "photo" : "photos"}
                    </strong>{" "}
                    representing{" "}
                    <strong className="font-semibold text-[#374049]">
                      {uploadedSpeciesCount.toLocaleString()} species
                    </strong>
                    .{" "}
                    <strong className="font-semibold text-[#01767A]">
                      {newUploadedSpeciesCount.toLocaleString()}
                    </strong>{" "}
                    {newUploadedSpeciesCount === 1 ? "was" : "were"} new to your
                    BioIndex.{" "}
                    {postUploadCommunityLoading ? (
                      <span className="text-[#374049]/45">
                        Checking your friends&#x2019; collections...
                      </span>
                    ) : !postUploadCommunityError ? (
                      <span>
                        Your friends also have{" "}
                        <strong className="font-semibold text-[#01767A]">
                          {speciesSharedWithFriendsCount.toLocaleString()}
                        </strong>{" "}
                        of the species in this upload.
                      </span>
                    ) : null}
                  </p>

                  {newGenusUnlocks.length > 0 && (
                    <div className="mt-4 border-t border-[#93CA87]/30 pt-4">
                      <button
                        type="button"
                        onClick={() =>
                          setTaxonomyUnlockDetailsOpen((open) => !open)
                        }
                        aria-expanded={taxonomyUnlockDetailsOpen}
                        className="flex w-full items-center justify-between gap-4 text-left"
                      >
                        <span className="text-sm font-semibold text-[#01767A]">
                          {newGenusUnlocks.length.toLocaleString()} new{" "}
                          {newGenusUnlocks.length === 1 ? "genus" : "genera"}{" "}
                          unlocked
                        </span>

                        <span className="inline-flex shrink-0 items-center gap-1.5 text-[11px] font-semibold text-[#374049]/50 transition hover:text-[#01767A]">
                          {taxonomyUnlockDetailsOpen ? "Hide" : "Details"}
                          <svg
                            viewBox="0 0 20 20"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                            className={`h-3.5 w-3.5 transition-transform duration-200 ${
                              taxonomyUnlockDetailsOpen ? "rotate-180" : ""
                            }`}
                          >
                            <path d="m6 8 4 4 4-4" />
                          </svg>
                        </span>
                      </button>

                      <div
                        className={`grid transition-all duration-300 ease-out ${
                          taxonomyUnlockDetailsOpen
                            ? "grid-rows-[1fr] opacity-100"
                            : "pointer-events-none grid-rows-[0fr] opacity-0"
                        }`}
                      >
                        <div className="min-h-0 overflow-hidden">
                          <div className="grid gap-x-6 gap-y-3 pt-4 sm:grid-cols-2">
                            {newGenusUnlocks.map((unlock) => (
                              <div
                                key={unlock.taxonName}
                                className="flex min-w-0 items-center gap-3"
                              >
                                <img
                                  src={iconUrl(`${unlock.taxonName}.png`)}
                                  onError={(e) => {
                                    e.currentTarget.onerror = null;
                                    e.currentTarget.src = defaultImg;
                                  }}
                                  alt={`${unlock.taxonName} genus icon`}
                                  className="h-11 w-11 shrink-0 rounded-xl border border-[#93CA87]/40 bg-white object-contain"
                                />

                                <span className="min-w-0 text-xs leading-4 text-[#374049]/55">
                                  <span
                                    className="block truncate text-base italic text-[#374049]"
                                    style={{
                                      fontFamily: "Cormorant Garamond",
                                    }}
                                  >
                                    {unlock.taxonName}
                                  </span>

                                  {unlock.broaderUnlocks.length > 0 && (
                                    <span className="block">
                                      Also your first in{" "}
                                      {unlock.broaderUnlocks.map(
                                        (broaderUnlock, index) => (
                                          <React.Fragment
                                            key={
                                              broaderUnlock.rank +
                                              ":" +
                                              broaderUnlock.taxonName
                                            }
                                          >
                                            {index > 0 &&
                                              (index ===
                                              unlock.broaderUnlocks.length - 1
                                                ? " and "
                                                : ", ")}
                                            <span className="font-medium text-[#374049]/75">
                                              {broaderUnlock.taxonName}
                                            </span>{" "}
                                            <span className="text-[#374049]/40">
                                              {broaderUnlock.rank}
                                            </span>
                                          </React.Fragment>
                                        ),
                                      )}
                                    </span>
                                  )}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {postUploadCommunityError && (
                    <p className="mt-3 text-xs text-amber-700">
                      {postUploadCommunityError}
                    </p>
                  )}
                </div>
              </section>

              {Object.entries(postUploadDefaultOptions).some(
                ([, forms]) => forms.length > 1,
              ) && (
                <section className="rounded-2xl border border-[#93CA87]/45 bg-[#FCFFF6] p-4 shadow-sm sm:p-5">
                  <h2
                    className="text-2xl font-semibold text-[#374049]"
                    style={{ fontFamily: "Cormorant Garamond" }}
                  >
                    Choose a display photo
                  </h2>
                  <p className="mt-1 text-xs leading-5 text-[#374049]/65">
                    For species with multiple photos, choose which one appears
                    first. Your choice is saved when you finish.
                  </p>
                  <div className="mt-4 space-y-4">
                    {uploadedBatchSpecies
                      .filter(
                        (species) =>
                          (postUploadDefaultOptions[species.speciesDocId] || [])
                            .length > 1,
                      )
                      .map((species) => {
                        const forms =
                          postUploadDefaultOptions[species.speciesDocId];
                        const selectedId =
                          pendingPostUploadDefaults[species.speciesDocId] ||
                          forms.find((form) => form.isDefault)?.id ||
                          forms[0].id;
                        return (
                          <div
                            key={species.speciesDocId}
                            className="rounded-xl border border-[#93CA87]/35 bg-white p-3"
                          >
                            <h3 className="mb-2 text-sm font-semibold italic text-[#374049]">
                              {species.scientificName}
                            </h3>
                            <div
                              className="flex gap-2 overflow-x-auto pb-1"
                              aria-label={`Display photo for ${species.scientificName}`}
                            >
                              {forms.map((form) => (
                                <button
                                  key={form.id}
                                  type="button"
                                  aria-pressed={selectedId === form.id}
                                  aria-label={`${form.id === selectedId ? "Selected" : "Choose"} display photo ${forms.indexOf(form) + 1} for ${species.scientificName}`}
                                  disabled={savingPostUploadCollections}
                                  onClick={() =>
                                    setPendingPostUploadDefaults((prev) => ({
                                      ...prev,
                                      [species.speciesDocId]: form.id,
                                    }))
                                  }
                                  className={`relative h-24 w-24 shrink-0 overflow-hidden rounded-xl border-2 bg-[#FCFFF6] transition sm:h-28 sm:w-28 ${
                                    selectedId === form.id
                                      ? "border-[#01767A] ring-2 ring-[#24C3BE]/30"
                                      : "border-[#93CA87]/40 hover:border-[#24C3BE]"
                                  }`}
                                >
                                  <img
                                    src={form.url}
                                    alt=""
                                    className="h-full w-full object-contain"
                                  />
                                  {selectedId === form.id && (
                                    <span className="absolute bottom-1 right-1 rounded-full bg-[#01767A] px-2 py-0.5 text-[10px] font-semibold text-white">
                                      &#x2713; Default
                                    </span>
                                  )}
                                </button>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </section>
              )}

              <section className="overflow-hidden rounded-3xl border border-[#93CA87]/45 bg-white shadow-sm">
                <div className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                  <div>
                    <h2
                      className="text-2xl font-semibold leading-none text-[#374049]"
                      style={{ fontFamily: "Cormorant Garamond" }}
                    >
                      Photo breakdown
                    </h2>
                    <p className="mt-1 text-xs text-[#374049]/50">
                      Review your upload and compare shared species.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() =>
                      setPostUploadCollectionToolsOpen((open) => !open)
                    }
                    className="inline-flex items-center justify-center gap-1.5 self-start text-xs font-semibold text-[#01767A] transition hover:text-[#24C3BE] sm:self-auto"
                  >
                    <span aria-hidden="true">
                      {postUploadCollectionToolsOpen ? "\u2212" : "+"}
                    </span>
                    Add photos to a collection
                  </button>
                </div>

                {postUploadCollectionToolsOpen && (
                  <div className="border-t border-[#93CA87]/25 bg-[#FCFFF6] px-5 py-4 sm:px-6">
                    <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
                      <label className="block min-w-0">
                        <span className="mb-1 block text-xs font-semibold text-[#374049]">
                          Collection
                        </span>
                        <select
                          value={postUploadCollection}
                          onChange={(event) => {
                            setPostUploadCollection(event.target.value);
                            setPostUploadCollectionError("");
                            setPostUploadCollectionMessage("");
                          }}
                          className="h-11 w-full rounded-xl border border-[#93CA87]/45 bg-white px-4 text-sm text-[#374049] outline-none focus:border-[#24C3BE] focus:ring-2 focus:ring-[#24C3BE]/20"
                        >
                          <option value="">Select a collection</option>
                          {collections.map((collectionName) => (
                            <option key={collectionName} value={collectionName}>
                              {collectionName}
                            </option>
                          ))}
                          <option value="__new__">
                            + Create a new collection
                          </option>
                        </select>
                      </label>

                      <p
                        className={[
                          "text-xs leading-5",
                          selectedCollectionNeedsPhoto
                            ? "text-red-600"
                            : "text-[#374049]/55",
                        ].join(" ")}
                      >
                        {postUploadCollection &&
                        postUploadCollection !== "__new__"
                          ? selectedPostUploadPhotoCount.toLocaleString() +
                            " " +
                            (selectedPostUploadPhotoCount === 1
                              ? "photo selected"
                              : "photos selected")
                          : "Choose a collection, then select photos below."}
                      </p>
                    </div>

                    {postUploadCollection === "__new__" && (
                      <div className="mt-3 grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
                        <input
                          type="text"
                          value={newPostUploadCollection}
                          onChange={(event) => {
                            setNewPostUploadCollection(event.target.value);
                            setPostUploadCollectionError("");
                          }}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") {
                              event.preventDefault();
                              createPostUploadCollection();
                            }
                          }}
                          maxLength={100}
                          placeholder="New collection name"
                          className="h-11 w-full rounded-xl border border-[#93CA87]/45 bg-white px-4 text-sm text-[#374049] outline-none focus:border-[#24C3BE] focus:ring-2 focus:ring-[#24C3BE]/20"
                          autoFocus
                        />

                        <button
                          type="button"
                          onClick={createPostUploadCollection}
                          disabled={savingPostUploadCollections}
                          className="h-11 rounded-xl bg-[#01767A] px-5 text-sm font-semibold text-white transition hover:bg-[#005f63] disabled:opacity-50"
                        >
                          Create
                        </button>
                      </div>
                    )}

                    {postUploadCollectionMessage && (
                      <p className="mt-3 text-xs text-[#01767A]">
                        {postUploadCollectionMessage}
                      </p>
                    )}

                    {postUploadCollectionError && (
                      <p role="alert" className="mt-3 text-xs text-red-700">
                        {postUploadCollectionError}
                      </p>
                    )}
                  </div>
                )}

                <div className="divide-y divide-[#93CA87]/25">
                  {postUploadCommunitySummary.map((species) => {
                    const friendMatches = Array.isArray(species.friendMatches)
                      ? species.friendMatches
                      : [];
                    const ownUploadedPhotos = uploadedBatchPhotos.filter(
                      (photo) => photo.speciesDocId === species.speciesDocId,
                    );
                    const comparisonExpanded =
                      !!expandedFriendComparisons[species.speciesDocId];
                    const friendPrompt =
                      friendMatches.length === 1
                        ? friendMatches[0].friendName + " also has this species"
                        : friendMatches.length.toLocaleString() +
                          " friends also have this species";
                    const ownCarouselSlides = ownUploadedPhotos.map(
                      (photo, photoIndex) => ({
                        source: "own",
                        ownerUid: auth.currentUser?.uid || "",
                        ownerName: "Your photo",
                        photoIndex,
                        photo,
                        form: photo.form || {},
                        url: photo.url,
                      }),
                    );
                    const friendCarouselSlides = friendMatches.flatMap(
                      (friendMatch) =>
                        (friendMatch.forms || []).map((form, formIndex) => ({
                          source: "friend",
                          ownerUid: friendMatch.friendUid,
                          ownerName: friendMatch.friendName,
                          ownerPhotoURL: friendMatch.friendPhotoURL,
                          photoIndex: formIndex,
                          form,
                          url: form.url,
                        })),
                    );
                    const carouselSlides = comparisonExpanded
                      ? [...ownCarouselSlides, ...friendCarouselSlides]
                      : ownCarouselSlides;
                    const requestedCarouselIndex =
                      postUploadCarouselIndexes[species.speciesDocId] || 0;
                    const carouselIndex = Math.min(
                      requestedCarouselIndex,
                      Math.max(0, carouselSlides.length - 1),
                    );
                    const activeSlide = carouselSlides[carouselIndex] || null;
                    const activeOwnPhoto =
                      activeSlide?.source === "own" ? activeSlide.photo : null;
                    const activePhotoAssignedToCollection =
                      !!activeOwnPhoto &&
                      !!postUploadCollection &&
                      postUploadCollection !== "__new__" &&
                      Array.isArray(activeOwnPhoto.collections) &&
                      activeOwnPhoto.collections.includes(postUploadCollection);

                    return (
                      <article
                        key={species.speciesDocId}
                        className="px-5 py-5 sm:px-6"
                      >
                        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                          {species.wasNewToUser ? (
                            <span className="rounded-full bg-[#24C3BE]/10 px-2.5 py-1 text-[10px] font-semibold text-[#01767A]">
                              New to you
                            </span>
                          ) : (
                            <span />
                          )}

                          {postUploadCommunityLoading ? (
                            <span className="inline-flex items-center gap-2 text-xs text-[#374049]/45">
                              <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-[#93CA87]/40 border-t-[#24C3BE]" />
                              Checking your friends&#x2019; collections...
                            </span>
                          ) : friendMatches.length > 0 ? (
                            <button
                              type="button"
                              onClick={() => {
                                const nextExpanded = !comparisonExpanded;

                                setExpandedFriendComparisons((previous) => ({
                                  ...previous,
                                  [species.speciesDocId]: nextExpanded,
                                }));

                                setPostUploadCarouselIndexes((previous) => ({
                                  ...previous,
                                  [species.speciesDocId]: nextExpanded
                                    ? ownCarouselSlides.length
                                    : 0,
                                }));
                              }}
                              aria-expanded={comparisonExpanded}
                              className="inline-flex items-center gap-1.5 text-left text-xs font-semibold text-[#01767A] transition hover:text-[#24C3BE]"
                            >
                              <span>{friendPrompt}.</span>
                              <span>
                                {comparisonExpanded
                                  ? "Show only mine"
                                  : "Compare photos"}
                              </span>
                            </button>
                          ) : postUploadFriendCount > 0 ? (
                            <span className="text-xs text-[#374049]/40">
                              None of your friends has recorded this species
                              yet.
                            </span>
                          ) : (
                            <span className="text-xs text-[#374049]/40">
                              Add friends to compare this species.
                            </span>
                          )}
                        </div>

                        {activeSlide && (
                          <ForumSpeciesPhotoCard
                            key={`${species.speciesDocId}_${activeSlide.ownerUid}_${activeSlide.form?.id || activeSlide.url}`}
                            imageURL={activeSlide.url}
                            speciesPhoto={{
                              speciesId: species.speciesDocId,
                              scientificName: species.scientificName,
                              commonName: species.commonName,
                              form: activeSlide.form,
                            }}
                            speciesPhotoRef={{
                              ownerUid: activeSlide.ownerUid,
                              speciesId: species.speciesDocId,
                              formId: activeSlide.form?.id || "",
                              formUrl: activeSlide.url,
                            }}
                            compact
                          />
                        )}

                        {activeSlide && (
                          <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            <div className="flex min-w-0 items-center gap-2">
                              {activeSlide.source === "friend" &&
                                (activeSlide.ownerPhotoURL ? (
                                  <img
                                    src={activeSlide.ownerPhotoURL}
                                    alt=""
                                    className="h-7 w-7 shrink-0 rounded-full border border-[#93CA87]/40 object-cover"
                                  />
                                ) : (
                                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#374049] text-[9px] font-semibold text-white">
                                    {activeSlide.ownerName
                                      .charAt(0)
                                      .toUpperCase() || "B"}
                                  </span>
                                ))}

                              <span className="truncate text-xs font-semibold text-[#374049]/65">
                                {activeSlide.source === "own"
                                  ? `Your photo ${activeSlide.photoIndex + 1}`
                                  : `${activeSlide.ownerName}\u2019s photo ${
                                      activeSlide.photoIndex + 1
                                    }`}
                              </span>
                            </div>

                            {carouselSlides.length > 1 && (
                              <div className="flex items-center gap-2 self-start sm:self-auto">
                                <button
                                  type="button"
                                  onClick={() =>
                                    setPostUploadCarouselIndexes(
                                      (previous) => ({
                                        ...previous,
                                        [species.speciesDocId]:
                                          (carouselIndex -
                                            1 +
                                            carouselSlides.length) %
                                          carouselSlides.length,
                                      }),
                                    )
                                  }
                                  aria-label="Previous comparison photo"
                                  className="flex h-8 w-8 items-center justify-center rounded-full border border-[#93CA87]/45 bg-white text-[#374049] transition hover:border-[#24C3BE] hover:text-[#01767A]"
                                >
                                  &#x2039;
                                </button>

                                <span className="min-w-10 text-center text-[10px] text-[#374049]/45">
                                  {carouselIndex + 1}/{carouselSlides.length}
                                </span>

                                <button
                                  type="button"
                                  onClick={() =>
                                    setPostUploadCarouselIndexes(
                                      (previous) => ({
                                        ...previous,
                                        [species.speciesDocId]:
                                          (carouselIndex + 1) %
                                          carouselSlides.length,
                                      }),
                                    )
                                  }
                                  aria-label="Next comparison photo"
                                  className="flex h-8 w-8 items-center justify-center rounded-full border border-[#93CA87]/45 bg-white text-[#374049] transition hover:border-[#24C3BE] hover:text-[#01767A]"
                                >
                                  &#x203A;
                                </button>
                              </div>
                            )}
                          </div>
                        )}

                        {postUploadCollectionToolsOpen &&
                          postUploadCollection &&
                          postUploadCollection !== "__new__" &&
                          activeOwnPhoto && (
                            <button
                              type="button"
                              onClick={() =>
                                toggleUploadedPhotoCollection(activeOwnPhoto)
                              }
                              disabled={savingPostUploadCollections}
                              className={`mt-3 inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-semibold transition disabled:opacity-50 ${
                                activePhotoAssignedToCollection
                                  ? "border-[#24C3BE] bg-[#24C3BE]/10 text-[#01767A]"
                                  : "border-[#93CA87]/45 bg-white text-[#374049]/65 hover:border-[#24C3BE] hover:text-[#01767A]"
                              }`}
                            >
                              <span aria-hidden="true">
                                {activePhotoAssignedToCollection
                                  ? "\u2713"
                                  : "+"}
                              </span>
                              {activePhotoAssignedToCollection
                                ? `Added to ${postUploadCollection}`
                                : `Add this photo to ${postUploadCollection}`}
                            </button>
                          )}
                      </article>
                    );
                  })}
                </div>

                <div className="flex flex-col-reverse gap-3 border-t border-[#93CA87]/30 bg-[#FCFFF6] px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                  <p className="text-center text-xs leading-5 text-[#374049]/50 sm:text-left">
                    {postUploadCollectionToolsOpen
                      ? "Collection selections are saved when you finish."
                      : "You can finish now or organize these photos first."}
                  </p>

                  <button
                    type="button"
                    onClick={finishPostUploadReview}
                    disabled={savingPostUploadCollections}
                    className="h-11 rounded-xl bg-[#374049] px-6 text-sm font-semibold text-white transition hover:bg-[#01767A] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {savingPostUploadCollections
                      ? "Saving..."
                      : "Save and Finish"}
                  </button>
                </div>
              </section>
            </div>
          )}
          {entries.length > 0 &&
            uploadView === "progress" &&
            !uploadSuccess &&
            (() => {
              const completedCount = entries.filter(
                (entry) => entry.uploadStatus === "success",
              ).length;
              const failedCount = entries.filter(
                (entry) => entry.uploadStatus === "error",
              ).length;
              const current =
                entries[Math.min(progressPhotoIndex, entries.length - 1)];
              const activeIndex = entries.indexOf(current);
              const activePercent = uploadPercents[current?.id] || 0;
              const liveEntry = entries.find(
                (entry) => entry.uploadStatus === "uploading",
              );
              const totalProgress = Math.min(
                100,
                Math.round(
                  (100 *
                    (completedCount +
                      (liveEntry
                        ? (uploadPercents[liveEntry.id] || 0) / 100
                        : 0))) /
                    entries.length,
                ),
              );
              const statusLabel = (entry) =>
                ({
                  success: "Uploaded",
                  uploading:
                    (uploadPercents[entry.id] || 0) >= 100
                      ? "Saving record"
                      : "Uploading",
                  error: "Failed",
                  pending: "Waiting",
                })[entry.uploadStatus] || "Waiting";
              return (
                <section
                  id="bioindex-upload-progress"
                  className="bioindex-upload-progress"
                  tabIndex={-1}
                  aria-label="Photo upload progress"
                >
                  <header className="bioindex-upload-progress-header">
                    <span className="bioindex-upload-progress-kicker">
                      BioIndex · Photo upload
                    </span>
                    <h1>
                      {isUploading
                        ? "Uploading your photos"
                        : "Upload interrupted"}
                    </h1>
                    <p>
                      {isUploading
                        ? "Your photos are being saved one at a time. You can follow each photo below."
                        : "Your completed photos are safe. Resume from the failed photo without uploading those photos twice."}
                    </p>
                  </header>
                  <div
                    className="bioindex-upload-progress-totals"
                    aria-live="polite"
                  >
                    <span>
                      <strong>{completedCount}</strong> uploaded
                    </span>
                    <span>
                      <strong>
                        {entries.length - completedCount - failedCount}
                      </strong>{" "}
                      {isUploading ? "remaining or in progress" : "waiting"}
                    </span>
                    <span className={failedCount ? "has-failures" : ""}>
                      <strong>{failedCount}</strong> failed
                    </span>
                  </div>
                  <div
                    className="bioindex-upload-progress-bar"
                    role="progressbar"
                    aria-label="Batch upload progress"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={totalProgress}
                  >
                    <span style={{ width: `${totalProgress}%` }} />
                  </div>
                  <div className="bioindex-upload-progress-caption">
                    <span>
                      {completedCount} of {entries.length} photos saved
                    </span>
                    <strong>{totalProgress}%</strong>
                  </div>

                  <div className="bioindex-upload-progress-feature">
                    <div className="bioindex-upload-progress-feature-image">
                      <img
                        src={current.previewUrl}
                        alt={
                          current.scientificName?.trim() ||
                          "Unidentified organism"
                        }
                      />
                      <span
                        className={`bioindex-upload-progress-feature-state is-${current.uploadStatus}`}
                      >
                        {current.uploadStatus === "uploading" && (
                          <span
                            className="bioindex-upload-progress-spinner"
                            aria-hidden="true"
                          />
                        )}
                        {statusLabel(current)}
                        {current.uploadStatus === "uploading" &&
                          activePercent < 100 &&
                          ` · ${activePercent}%`}
                      </span>
                    </div>
                    <div className="bioindex-upload-progress-feature-info">
                      <span className="bioindex-upload-progress-feature-number">
                        Photo {activeIndex + 1} of {entries.length}
                      </span>
                      <h2>
                        <i>
                          {current.scientificName?.trim() || "Unidentified"}
                        </i>
                      </h2>
                      <p title={current.file?.name}>{current.file?.name}</p>
                      <div className="bioindex-upload-progress-feature-pager">
                        <button
                          type="button"
                          onClick={() =>
                            setProgressPhotoIndex(Math.max(0, activeIndex - 1))
                          }
                          disabled={activeIndex === 0}
                          aria-label="Previous progress photo"
                        >
                          ←
                        </button>
                        <span>
                          {activeIndex + 1} / {entries.length}
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            setProgressPhotoIndex(
                              Math.min(entries.length - 1, activeIndex + 1),
                            )
                          }
                          disabled={activeIndex === entries.length - 1}
                          aria-label="Next progress photo"
                        >
                          →
                        </button>
                      </div>
                      {current.uploadStatus === "uploading" && (
                        <div
                          className="bioindex-upload-progress-image-meter"
                          aria-label={`${activePercent}% of photo transferred`}
                        >
                          <span style={{ width: `${activePercent}%` }} />
                        </div>
                      )}
                      {current.uploadStatus === "error" && (
                        <p className="bioindex-upload-progress-retry-hint">
                          This photo will be retried first. Earlier uploads will
                          be skipped.
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="bioindex-upload-progress-gallery-heading">
                    <strong>Photo status</strong>
                    <span>Swipe to browse the batch</span>
                  </div>
                  <div
                    className="bioindex-upload-progress-gallery"
                    ref={progressCarouselRef}
                    role="group"
                    aria-label="Upload status for each photo"
                  >
                    {entries.map((entry, index) => (
                      <button
                        key={entry.id}
                        type="button"
                        className={`bioindex-upload-progress-tile ${progressPhotoIndex === index ? "is-selected" : ""} is-${entry.uploadStatus}`}
                        onClick={() => setProgressPhotoIndex(index)}
                        data-progress-photo-active={
                          progressPhotoIndex === index ? "true" : "false"
                        }
                        aria-pressed={progressPhotoIndex === index}
                        aria-label={`Photo ${index + 1}: ${statusLabel(entry)}`}
                      >
                        <span className="bioindex-upload-progress-tile-image">
                          <img src={entry.previewUrl} alt="" />
                          <span className="bioindex-upload-progress-tile-number">
                            {index + 1}
                          </span>
                        </span>
                        <span
                          className={`bioindex-upload-progress-tile-status is-${entry.uploadStatus}`}
                        >
                          {entry.uploadStatus === "success"
                            ? "✓ "
                            : entry.uploadStatus === "error"
                              ? "! "
                              : ""}
                          {statusLabel(entry)}
                          {entry.uploadStatus === "uploading" &&
                            (uploadPercents[entry.id] || 0) < 100 &&
                            ` ${uploadPercents[entry.id] || 0}%`}
                        </span>
                      </button>
                    ))}
                  </div>
                  {!isUploading && uploadError && (
                    <div
                      className="bioindex-upload-progress-failure"
                      role="alert"
                    >
                      <strong>Upload paused</strong>
                      <p>{uploadError}</p>
                      <div className="bioindex-upload-progress-failure-actions">
                        <button
                          type="button"
                          className="bioindex-upload-progress-retry"
                          onClick={handleUpload}
                        >
                          Retry Upload
                        </button>
                        <button
                          type="button"
                          className="bioindex-upload-progress-edit"
                          onClick={() => {
                            const firstUnfinished = entries.findIndex(
                              (entry) => entry.uploadStatus !== "success",
                            );
                            setActivePhotoIndex(Math.max(0, firstUnfinished));
                            setUploadView("editing");
                          }}
                        >
                          Review remaining details
                        </button>
                      </div>
                    </div>
                  )}
                </section>
              );
            })()}
          {uploadError && uploadView === "editing" && (
            <div
              role="alert"
              className="rounded-2xl border border-red-200 bg-red-50 p-4 text-center"
            >
              <p className="text-sm leading-5 text-red-700">{uploadError}</p>

              {entries.length > 0 && (
                <button
                  type="button"
                  onClick={handleUpload}
                  disabled={isUploading}
                  className="mt-3 rounded-xl bg-[#374049] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#01767A] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isUploading ? "Trying Again..." : "Try Upload Again"}
                </button>
              )}
            </div>
          )}
          {fileLimitError && (
            <div className="text-center text-sm text-red-700">
              {fileLimitError}
            </div> // file limit error
          )}
          {entries.length === 0 && !uploadSuccess && (
            <div
              {...getRootProps()}
              className={`bioindex-upload-dropzone group flex min-h-[320px] cursor-pointer flex-col items-center justify-center rounded-3xl border-2 border-dashed px-6 py-10 text-center outline-none transition-colors duration-200 focus-visible:ring-4 focus-visible:ring-[#24C3BE]/20 sm:px-12 sm:py-14 ${
                isDragActive
                  ? "border-[#24C3BE] bg-[#24C3BE]/10"
                  : "border-[#93CA87]/70 bg-[#FCFFF6] hover:border-[#24C3BE] hover:bg-[#24C3BE]/5"
              }`}
            >
              <input {...getInputProps()} />

              <img
                src="/photoIcon.png"
                alt=""
                aria-hidden="true"
                className="h-16 w-16 object-contain transition-transform duration-200 group-hover:scale-105"
              />

              <h2
                className="mt-5 text-3xl font-semibold text-[#374049] sm:text-4xl"
                style={{ fontFamily: "Cormorant Garamond" }}
              >
                {isDragActive ? "Drop your photos here" : "What did you find?"}
              </h2>

              <p className="mt-3 max-w-2xl text-sm leading-6 text-[#374049]/75 sm:text-base sm:leading-7">
                Upload up to 10 photos of plants, animals, fungi, or other
                organisms. You'll identify each species and add the observation
                details in the next step.
              </p>

              <span className="bioindex-upload-choose mt-6 rounded-xl bg-[#24C3BE] px-6 py-3 text-sm font-semibold tracking-wide text-white shadow-sm transition-colors duration-200 group-hover:bg-[#1fa9a5]">
                Choose Photos
              </span>

              <span className="mt-3 hidden text-sm text-[#374049]/60 sm:block">
                or drag and drop them here
              </span>

              <span className="mt-2 text-xs uppercase tracking-[0.14em] text-[#374049]/45">
                JPG, PNG, or HEIC
              </span>
            </div>
          )}
          {entries.length > 0 && uploadView === "editing" && !uploadSuccess && (
            <div className="bioindex-upload-batch">
              {/* A single task bar replaces the scattered count / tutorial /
    full-width Shared Details accordion. */}
              <div className="bioindex-upload-workflow-toolbar">
                <button
                  type="button"
                  className="bioindex-upload-selected-summary"
                  onClick={() => setPhotoTrayOpen((open) => !open)}
                  aria-expanded={photoTrayOpen}
                  aria-controls="bioindex-upload-photo-rail"
                  title="Choose a photo from this batch"
                >
                  <span
                    className="bioindex-upload-workflow-icon"
                    aria-hidden="true"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="3" y="5" width="15" height="15" rx="2" />
                      <path d="m6 16 4-4 3 3 2-2 3 3M8 8h.01M20 9v12" />
                    </svg>
                  </span>
                  <span className="bioindex-upload-workflow-summary-text">
                    <strong>
                      {entries.length} selected photo
                      {entries.length === 1 ? "" : "s"}
                    </strong>
                    <small>Choose a photo to edit</small>
                  </span>
                  <span
                    className="bioindex-upload-mobile-rail-chevron"
                    aria-hidden="true"
                  >
                    ⌄
                  </span>
                </button>

                <div className="bioindex-upload-workflow-actions">
                  <button
                    type="button"
                    onClick={beginSharedDetails}
                    className="bioindex-upload-workflow-button bioindex-upload-shared-compact"
                    aria-haspopup="dialog"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <rect x="3" y="3" width="8" height="8" rx="1.5" />
                      <rect x="13" y="3" width="8" height="8" rx="1.5" />
                      <rect x="3" y="13" width="8" height="8" rx="1.5" />
                      <path d="M17 13v8M13 17h8" />
                    </svg>
                    <span>Shared details</span>
                  </button>
                  <button
                    ref={tutorialLaunchRef}
                    type="button"
                    onClick={() => setTutorialStep(0)}
                    aria-haspopup="dialog"
                    className="bioindex-upload-workflow-button bioindex-upload-help-button"
                    title="Upload tutorial"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M12 17h.01M9.3 9.1a3 3 0 1 1 5.1 2.1c-1.2 1-2.4 1.6-2.4 3" />
                      <circle cx="12" cy="12" r="9" />
                    </svg>
                    <span>Tutorial</span>
                  </button>
                </div>
              </div>

              {/* On phones, show all selected photos as a compact swipeable strip.
    The large desktop thumbnail sidebar is unchanged. */}
              <div
                className="bioindex-upload-mobile-photo-carousel"
                aria-label="Selected photos"
              >
                <div className="bioindex-upload-mobile-carousel-heading">
                  <strong>Photos ({entries.length})</strong>
                  <span aria-live="polite">
                    {activePhotoIndex + 1} / {entries.length}
                  </span>
                </div>
                <div
                  ref={mobilePhotoCarouselRef}
                  className="bioindex-upload-mobile-carousel-track"
                  role="group"
                  aria-label="Choose a photo to edit"
                >
                  {entries.map((batchEntry, batchIndex) => {
                    const isCurrent = activePhotoIndex === batchIndex;

                    return (
                      <button
                        key={batchEntry.id}
                        type="button"
                        onClick={() => selectUploadPhoto(batchIndex)}
                        disabled={
                          isUploading || batchEntry.uploadStatus === "success"
                        }
                        className={`bioindex-upload-mobile-carousel-photo ${isCurrent ? "is-active" : ""}`}
                        data-upload-photo-active={isCurrent ? "true" : "false"}
                        aria-pressed={isCurrent}
                        aria-label={`Photo ${batchIndex + 1} of ${entries.length}${isCurrent ? ", selected" : ""}`}
                      >
                        <img src={batchEntry.previewUrl} alt="" />
                        <span
                          className="bioindex-upload-mobile-carousel-number"
                          aria-hidden="true"
                        >
                          {batchIndex + 1}
                        </span>
                        {isCurrent && (
                          <span
                            className="bioindex-upload-mobile-carousel-check"
                            aria-hidden="true"
                          >
                            ✓
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              <section
                ref={uploadEditorRef}
                className="bioindex-upload-workspace"
                aria-label="Selected photo editor"
              >
                <aside
                  id="bioindex-upload-photo-rail"
                  className={`bioindex-upload-photo-rail ${photoTrayOpen ? "is-open" : ""}`}
                  aria-label="Selected photos"
                >
                  <div className="bioindex-upload-rail-heading">
                    <strong>Selected Photos</strong>
                    <span>{entries.length} photos</span>
                  </div>
                  <div className="bioindex-upload-rail-items">
                    {entries.map((batchEntry, batchIndex) => (
                      <button
                        key={batchEntry.id}
                        type="button"
                        onClick={() => selectUploadPhoto(batchIndex)}
                        disabled={
                          isUploading || batchEntry.uploadStatus === "success"
                        }
                        className={`bioindex-upload-rail-photo ${activePhotoIndex === batchIndex ? "is-active" : ""}`}
                        aria-current={
                          activePhotoIndex === batchIndex ? "step" : undefined
                        }
                        aria-label={`Edit photo ${batchIndex + 1}`}
                      >
                        <span className="bioindex-upload-rail-thumbnail">
                          <img src={batchEntry.previewUrl} alt="" />
                          <span className="bioindex-upload-rail-number">
                            {batchIndex + 1}
                          </span>
                        </span>
                        <span className="bioindex-upload-rail-caption">
                          <strong>Photo {batchIndex + 1}</strong>
                          <small>
                            {batchEntry.scientificName?.trim() || "Add details"}
                          </small>
                        </span>
                      </button>
                    ))}
                  </div>
                </aside>

                <div className="bioindex-upload-editor-stack">
                  {entries.map((entry, index) => (
                    <div
                      key={entry.id}
                      className="bioindex-upload-entry"
                      style={{
                        display:
                          activePhotoIndex === index ? undefined : "none",
                      }}
                    >
                      <div className="bioindex-upload-editor-header bioindex-upload-mobile-only-header md:hidden">
                        <div className="bioindex-upload-current-photo-heading">
                          <img
                            className="bioindex-upload-current-photo-thumb"
                            src={entry.previewUrl}
                            alt=""
                          />
                          <div>
                            <h3>
                              Photo {index + 1} of {entries.length}
                            </h3>
                            <small>
                              {entry.scientificName?.trim() ||
                                "Enter photo details"}
                            </small>
                          </div>
                        </div>
                        <div
                          className="bioindex-upload-editor-arrows"
                          aria-label="Navigate selected photos"
                        >
                          <button
                            type="button"
                            disabled={
                              isUploading ||
                              index === 0 ||
                              entries[index - 1]?.uploadStatus === "success"
                            }
                            onClick={() => selectUploadPhoto(index - 1)}
                            aria-label="Previous photo"
                          >
                            ‹
                          </button>
                          <button
                            type="button"
                            disabled={
                              isUploading || index === entries.length - 1
                            }
                            onClick={() => selectUploadPhoto(index + 1)}
                            aria-label="Next photo"
                          >
                            ›
                          </button>
                        </div>
                      </div>

                      {/* ================= LEFT COLUMN: IMAGE + STATUS + ACTIONS ================= */}
                      <div className="bioindex-upload-photo-column">
                        {/* Image preview */}
                        <div className="bioindex-upload-photo-preview">
                          <div className="bioindex-upload-photo-image-wrap">
                            <img
                              src={entry.previewUrl}
                              alt="preview"
                              className="bioindex-upload-preview-image"
                            />

                            <button
                              type="button"
                              onClick={() => removeEntry(index)}
                              disabled={isUploading}
                              aria-label={`Remove photo ${index + 1}`}
                              title="Remove photo"
                              className="absolute -right-2 -top-2 z-10 flex h-8 w-8 items-center justify-center rounded-full border border-[#374049]/25 bg-white text-base font-bold leading-none text-[#374049] shadow-md transition hover:border-rose-300 hover:bg-rose-50 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              &#x2715;
                            </button>
                          </div>
                        </div>

                        {/* Upload state indicators - tied to entry.uploadStatus */}
                        {entry.uploadStatus === "uploading" && (
                          <div className="text-xs text-blue-600 mt-1">
                            Uploading...
                          </div>
                        )}
                        {entry.uploadStatus === "success" && (
                          <div className="text-xs text-green-600 mt-1">
                            Uploaded &#x2713;
                          </div>
                        )}
                        {entry.uploadStatus === "error" && (
                          <div className="text-xs text-red-600 mt-1">
                            Upload failed
                          </div>
                        )}

                        {/* Compact identification tools, directly below the photo. */}
                        <div
                          className="bioindex-upload-photo-tools"
                          role="toolbar"
                          aria-label="Photo identification tools"
                        >
                          <button
                            type="button"
                            onClick={() => suggestWithGemini(index)}
                            disabled={
                              isUploading || entry.geminiStatus === "loading"
                            }
                            className="bioindex-upload-photo-tool"
                            data-tooltip={
                              entry.geminiStatus === "loading"
                                ? "Consulting Gemini..."
                                : "Suggest ID with Gemini"
                            }
                            aria-label={
                              entry.geminiStatus === "loading"
                                ? "Consulting Gemini..."
                                : "Suggest ID with Gemini"
                            }
                          >
                            <img
                              src={geminiLogo}
                              alt=""
                              className="bioindex-upload-tool-logo"
                            />
                          </button>

                          {entry.scientificName?.trim() && (
                            <a
                              href={`https://www.inaturalist.org/search?q=${encodeURIComponent(entry.scientificName.trim())}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="bioindex-upload-photo-tool"
                              data-tooltip="Check on iNaturalist"
                              aria-label="Check on iNaturalist"
                            >
                              <img
                                src={inatLogo}
                                alt=""
                                className="bioindex-upload-tool-logo"
                              />
                            </a>
                          )}
                        </div>
                        {/* ================= EXISTING FORMS PREVIEW ================= */}
                        {entry.speciesExists && // only if species already exists in Firestore
                          entry.existingForms.length > 0 && // must have existing images
                          activeField[index] !== "scientific" && // avoid interrupting typing
                          activeField[index] !== "common" && (
                            <div
                              className="mt-4 p-3 rounded-2xl border border-[#93ca87]/40 bg-slate-50 hover:bg-slate-100 hover:shadow-sm transition cursor-pointer"
                              onClick={() => setOpenExistingIndex(index)} // opens modal for managing forms
                            >
                              {/* Scrollable row of thumbnails */}
                              <div className="flex justify-center gap-2 overflow-x-auto px-1 py-1">
                                {entry.existingForms.slice(0, 10).map(
                                  (
                                    f,
                                    i, // limit preview to 10
                                  ) => (
                                    <div
                                      key={f.id || i}
                                      className={`relative h-12 w-12 rounded-md border overflow-hidden flex items-center justify-center ${
                                        (entry.formsToDelete || []).includes(
                                          f.id,
                                        )
                                          ? "border-red-200 bg-white text-red-700"
                                          : "border-[#93CA87]/25 bg-white text-[#374049]/55"
                                      }`}
                                    >
                                      {/* Spinner shown until image loads */}
                                      {!(
                                        entry.existingFormsLoaded &&
                                        entry.existingFormsLoaded[i]
                                      ) && (
                                        <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                                      )}

                                      <img
                                        src={f.url} // stored Firebase image URL
                                        alt=""
                                        onLoad={() => {
                                          // mark this specific thumbnail as loaded (per-index tracking)
                                          setEntries((prev) =>
                                            prev.map((e, idx) =>
                                              idx === index
                                                ? {
                                                    ...e,
                                                    existingFormsLoaded: {
                                                      ...e.existingFormsLoaded,
                                                      [i]: true, // flag this image as loaded
                                                    },
                                                  }
                                                : e,
                                            ),
                                          );
                                        }}
                                        className={`absolute inset-0 h-full w-full object-contain transition-opacity ${
                                          entry.existingFormsLoaded &&
                                          entry.existingFormsLoaded[i]
                                            ? "opacity-100" // fade in once loaded
                                            : "opacity-0"
                                        }`}
                                      />

                                      {/* Visual overlay if this form is marked for deletion */}
                                      {(entry.formsToDelete || []).includes(
                                        f.id,
                                      ) && (
                                        <div className="absolute inset-0 bg-red-500/20" />
                                      )}
                                    </div>
                                  ),
                                )}
                              </div>

                              {/* Summary text with plural handling */}
                              <div className="mt-2 text-center text-[11px] leading-tight text-slate-500">
                                Click to view and manage{" "}
                                {entry.existingForms.length} photo
                                {entry.existingForms.length > 1 ? "s" : ""}
                              </div>
                            </div>
                          )}
                      </div>

                      <div className="bioindex-upload-editor-fields min-w-0 flex-1 space-y-3 sm:space-y-4">
                        <div className="grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-2">
                          {/* RIGHT COLUMN: metadata inputs */}
                          {/* ================= SCIENTIFIC NAME INPUT ================= */}
                          <div className="relative min-w-0">
                            <div className="mb-1 text-xs font-semibold text-slate-600">
                              Scientific Name
                            </div>

                            <input
                              id={`scientific-name-${entry.id}`}
                              aria-invalid={
                                highlightMissingNames &&
                                !entry.scientificName?.trim()
                              }
                              type="text"
                              placeholder="e.g., Momotus aequatorialis"
                              value={entry.scientificName} // controlled input
                              onChange={(e) => {
                                const value = e.target.value;

                                // Update this entry and reset dependent state
                                setEntries((prev) =>
                                  prev.map((entry, i) =>
                                    i === index
                                      ? {
                                          ...entry,
                                          scientificName: value, // update name
                                          formLabel:
                                            resolveLifeformKingdom(
                                              entry.scientificName,
                                            ) &&
                                            resolveLifeformKingdom(value) &&
                                            resolveLifeformKingdom(
                                              entry.scientificName,
                                            ) !== resolveLifeformKingdom(value)
                                              ? ""
                                              : entry.formLabel,
                                          speciesExists: false, // invalidate previous DB check
                                          existingForms: [], // clear cached forms
                                          existingFormsLoaded: {}, // reset thumbnail loading
                                        }
                                      : entry,
                                  ),
                                );

                                // Trigger autocomplete suggestions
                                updateSuggestions(value, index, "scientific");
                              }}
                              onFocus={() =>
                                setActiveField((prev) => ({
                                  ...prev,
                                  [index]: "scientific", // mark this field as active
                                }))
                              }
                              onKeyDown={
                                (e) => handleSuggestionKeyDown(e, index) // keyboard nav (up down Enter)
                              }
                              className={`block h-11 w-full min-w-0 truncate rounded-xl border px-3 text-sm italic outline-none transition focus:ring-2 sm:h-[48px] sm:px-4 ${
                                highlightMissingNames &&
                                !entry.scientificName?.trim()
                                  ? "border-rose-500 bg-rose-50 focus:border-rose-500 focus:ring-rose-200"
                                  : "border-[#93ca87]/40 bg-white focus:border-[#24c3be] focus:ring-[#24c3be]/25"
                              }`}
                              disabled={isUploading}
                            />

                            {/* ================= AUTOCOMPLETE DROPDOWN ================= */}
                            {activeField[index] === "scientific" && // only when focused
                              suggestions[index] &&
                              suggestions[index].length > 0 && (
                                <div
                                  className="absolute top-full left-0 z-20 w-full border border-[#93ca87]/40 rounded-xl mt-1 bg-white max-h-40 overflow-y-auto shadow-sm"
                                  // Mobile scroll optimizations (prevent parent scroll hijack)
                                  style={{
                                    WebkitOverflowScrolling: "touch",
                                    overscrollBehavior: "contain",
                                    touchAction: "pan-y",
                                  }}
                                  onTouchStart={(e) => {
                                    e.stopPropagation(); // isolate touch events
                                  }}
                                  onTouchMove={(e) => {
                                    const el = e.currentTarget;

                                    const isAtTop = el.scrollTop === 0;
                                    const isAtBottom =
                                      el.scrollHeight - el.scrollTop ===
                                      el.clientHeight;

                                    const touch = e.touches[0];
                                    const prevY = el._lastY || touch.clientY;
                                    const deltaY = touch.clientY - prevY;
                                    el._lastY = touch.clientY;

                                    // Prevent overscroll "bounce" leaking to parent container
                                    if (
                                      (isAtTop && deltaY > 0) ||
                                      (isAtBottom && deltaY < 0)
                                    ) {
                                      e.preventDefault();
                                    }

                                    e.stopPropagation();
                                  }}
                                  onTouchEnd={(e) => {
                                    e.currentTarget._lastY = null; // reset tracking
                                  }}
                                  onWheel={(e) => {
                                    e.stopPropagation(); // prevent parent scroll
                                  }}
                                >
                                  {suggestions[index].map((sp, i) => (
                                    <div
                                      key={`${sp.scientific}-${sp.common || "no-common"}-${i}`}
                                      onClick={() =>
                                        selectSuggestion(sp, index)
                                      } // apply suggestion
                                      className={`px-3 py-2 text-sm cursor-pointer ${
                                        activeSuggestion[index] === i
                                          ? "bg-slate-200" // highlighted via keyboard
                                          : "hover:bg-slate-100"
                                      }`}
                                    >
                                      {/* Scientific name (italicized) */}
                                      <span className="italic">
                                        {sp.scientific}
                                      </span>

                                      {/* Common name */}
                                      <span className="text-slate-500 ml-2">
                                        &#x2014; {sp.common}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                          </div>

                          {/* ================= COMMON NAME INPUT ================= */}
                          <div className="relative min-w-0">
                            <div className="mb-1 text-xs font-semibold text-slate-600">
                              Common Name
                            </div>

                            <input
                              type="text"
                              placeholder="e.g., Andean Motmot"
                              value={entry.commonName}
                              onChange={(e) => {
                                const value = e.target.value;

                                updateEntry(index, "commonName", value);
                                updateSuggestions(value, index, "common");
                              }}
                              onFocus={() =>
                                setActiveField((prev) => ({
                                  ...prev,
                                  [index]: "common",
                                }))
                              }
                              onKeyDown={(e) =>
                                handleSuggestionKeyDown(e, index)
                              }
                              className="block h-11 w-full min-w-0 truncate rounded-xl border border-[#93ca87]/40 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#24c3be]/25 sm:h-[48px] sm:px-4"
                              disabled={isUploading}
                            />

                            {activeField[index] === "common" &&
                              suggestions[index] &&
                              suggestions[index].length > 0 && (
                                <div
                                  className="absolute top-full left-0 z-20 w-full border border-[#93ca87]/40 rounded-xl mt-1 bg-white max-h-40 overflow-y-auto shadow-sm"
                                  style={{
                                    WebkitOverflowScrolling: "touch",
                                    overscrollBehavior: "contain",
                                    touchAction: "pan-y",
                                  }}
                                  onTouchStart={(e) => e.stopPropagation()}
                                  onTouchMove={(e) => {
                                    const el = e.currentTarget;

                                    const isAtTop = el.scrollTop === 0;
                                    const isAtBottom =
                                      el.scrollHeight - el.scrollTop ===
                                      el.clientHeight;

                                    const touch = e.touches[0];
                                    const prevY = el._lastY || touch.clientY;
                                    const deltaY = touch.clientY - prevY;
                                    el._lastY = touch.clientY;

                                    if (
                                      (isAtTop && deltaY > 0) ||
                                      (isAtBottom && deltaY < 0)
                                    ) {
                                      e.preventDefault();
                                    }

                                    e.stopPropagation();
                                  }}
                                  onTouchEnd={(e) => {
                                    e.currentTarget._lastY = null;
                                  }}
                                  onWheel={(e) => e.stopPropagation()}
                                >
                                  {suggestions[index].map((sp, i) => (
                                    <div
                                      key={`${sp.scientific}-${sp.common || "no-common"}-${i}`}
                                      onClick={() =>
                                        selectSuggestion(sp, index)
                                      }
                                      className={`px-3 py-2 text-sm cursor-pointer ${
                                        activeSuggestion[index] === i
                                          ? "bg-slate-200"
                                          : "hover:bg-slate-100"
                                      }`}
                                    >
                                      <span className="italic">
                                        {sp.scientific}
                                      </span>

                                      <span className="text-slate-500 ml-2">
                                        &#x2014; {sp.common}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                          </div>
                        </div>

                        {/* Lifeform is paired with the optional-detail buttons below */}
                        <div className="grid min-w-0 grid-cols-1 items-end gap-3 sm:gap-4 md:grid-cols-[9fr_11fr]">
                          {/* responsive layout: single column -> two columns */}
                          <div className="flex flex-col h-full">
                            <div className="mb-1 text-xs font-semibold text-slate-600">
                              Date
                            </div>
                            {/* Internal grid for day / month / year with custom column widths */}
                            <div className="grid grid-cols-[0.9fr_1.6fr_1fr] gap-2 mt-auto">
                              {/* -------- DAY SELECT -------- */}
                              <select
                                value={entry.date?.day || "NA"} // safely access day; fallback if missing
                                onChange={(e) =>
                                  updateEntry(index, "date", {
                                    ...normalizeDate(entry.date), // ensure full {day, month, year} object
                                    day: e.target.value, // update only the day field
                                  })
                                }
                                className="w-full h-[48px] border border-[#93ca87]/40 rounded-xl px-3 py-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#24c3be]/25"
                                disabled={isUploading} // prevent edits during upload
                              >
                                {DAYS.map((d) => (
                                  <option key={d} value={d}>
                                    {d} {/* includes "NA" + "01"-"31" */}
                                  </option>
                                ))}
                              </select>
                              {/* -------- MONTH SELECT -------- */}
                              <select
                                value={entry.date?.month || "NA"} // safe access; fallback if missing
                                onChange={(e) =>
                                  updateEntry(index, "date", {
                                    ...normalizeDate(entry.date), // preserve day + year
                                    month: e.target.value, // update only month
                                  })
                                }
                                className="w-full h-[48px] border border-[#93ca87]/40 rounded-xl px-4 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#24c3be]/25"
                                disabled={isUploading} // lock during upload
                              >
                                {MONTHS.map((m) => (
                                  <option key={m.value} value={m.value}>
                                    {m.label}
                                  </option>
                                ))}
                              </select>
                              {/* -------- YEAR SELECT -------- */}
                              <select
                                value={entry.date?.year || "NA"} // safe access; fallback if missing
                                onChange={(e) =>
                                  updateEntry(index, "date", {
                                    ...normalizeDate(entry.date), // preserve day + month
                                    year: e.target.value, // update only year
                                  })
                                }
                                className="w-full h-[48px] border border-[#93ca87]/40 rounded-xl px-4 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#24c3be]/25"
                                disabled={isUploading} // prevent changes mid-upload
                              >
                                {YEARS.map((y) => (
                                  <option key={y} value={y}>
                                    {y} {/* "NA" + dynamic last ~80 years */}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                          {/* ================= LOCATION (per-entry) ================= */}
                          <div className="flex min-w-0 flex-col h-full">
                            <div className="mb-1 text-xs font-semibold text-slate-600">
                              Location
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                clearNameEditing(index);
                                setLocationModal({ scope: "entry", index });
                              }}
                              className="mt-auto block w-full min-w-0 h-[48px] overflow-hidden border border-[#93ca87]/40 rounded-xl px-4 text-sm bg-white text-left hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#24c3be]/25 disabled:opacity-50"
                              disabled={isUploading}
                              title={entry.location || "Select location on map"}
                            >
                              <span className="block w-full truncate">
                                {entry.location || "Select location on map"}
                              </span>
                            </button>
                          </div>
                        </div>
                        {/* ================= LIFEFORM + OPTIONAL METADATA ================= */}
                        <div className="grid grid-cols-1 items-start gap-3 sm:gap-4 md:grid-cols-[2fr_3fr]">
                          {/* Kingdom-aware Lifeform selection */}
                          <LifeformPicker
                            scientificName={entry.scientificName}
                            value={entry.formLabel}
                            onChange={(value) => {
                              clearNameEditing(index);
                              updateEntry(index, "formLabel", value);
                            }}
                            selectedKingdom={entry.lifeformKingdom || ""}
                            onKingdomChange={(value) => {
                              clearNameEditing(index);
                              setEntries((prev) =>
                                prev.map((item, i) =>
                                  i === index
                                    ? {
                                        ...item,
                                        lifeformKingdom: value,
                                        formLabel: "",
                                      }
                                    : item,
                                ),
                              );
                            }}
                            disabled={isUploading}
                          />

                          {/* -------- DIRECT OPTIONAL FIELDS -------- */}
                          <div className="relative min-w-0">
                            <div className="mb-1 text-xs font-semibold text-slate-600">
                              Optional details
                            </div>

                            <div className="grid h-[48px] grid-cols-2 gap-2">
                              <button
                                type="button"
                                onClick={() =>
                                  setEntries((prev) =>
                                    prev.map((e, i) =>
                                      i === index
                                        ? {
                                            ...e,
                                            showVariantField:
                                              !e.showVariantField,
                                          }
                                        : e,
                                    ),
                                  )
                                }
                                className={`h-[48px] rounded-xl border px-3 text-sm font-medium transition focus:outline-none focus:ring-2 focus:ring-[#24c3be]/25 ${
                                  entry.showVariantField
                                    ? "border-[#24c3be] bg-[#24c3be] text-white"
                                    : "border-[#93ca87]/40 bg-white text-[#374049] hover:bg-[#24c3be]/5"
                                }`}
                                disabled={isUploading}
                              >
                                {entry.showVariantField
                                  ? "\u2713 Variant"
                                  : "+ Variant"}
                              </button>

                              <button
                                type="button"
                                onClick={() =>
                                  setEntries((prev) =>
                                    prev.map((e, i) =>
                                      i === index
                                        ? {
                                            ...e,
                                            showNotesField: !e.showNotesField,
                                          }
                                        : e,
                                    ),
                                  )
                                }
                                className={`h-[48px] rounded-xl border px-3 text-sm font-medium transition focus:outline-none focus:ring-2 focus:ring-[#24c3be]/25 ${
                                  entry.showNotesField
                                    ? "border-[#24c3be] bg-[#24c3be] text-white"
                                    : "border-[#93ca87]/40 bg-white text-[#374049] hover:bg-[#24c3be]/5"
                                }`}
                                disabled={isUploading}
                              >
                                {entry.showNotesField
                                  ? "\u2713 Notes"
                                  : "+ Notes"}
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* ================= SELECTED OPTIONAL FIELDS ================= */}
                        {(entry.showVariantField || entry.showNotesField) && (
                          <div className="grid min-w-0 grid-cols-1 gap-4 rounded-2xl border border-[#93CA87]/35 bg-[#FCFFF6] p-4 md:grid-cols-2">
                            {/* -------- VARIANT FIELD -------- */}
                            {entry.showVariantField && (
                              <div className="relative min-w-0">
                                <div className="mb-1 flex items-center justify-between gap-3">
                                  <div className="text-xs font-semibold text-slate-600">
                                    Variant
                                  </div>

                                  <button
                                    type="button"
                                    onClick={() =>
                                      setEntries((prev) =>
                                        prev.map((e, i) =>
                                          i === index
                                            ? {
                                                ...e,
                                                showVariantField: false,
                                              }
                                            : e,
                                        ),
                                      )
                                    }
                                    className="text-xs text-slate-400 transition hover:text-red-500"
                                    title="Remove Variant field"
                                  >
                                    &#x2715;
                                  </button>
                                </div>

                                <input
                                  type="text"
                                  placeholder="e.g., bicolor"
                                  value={entry.variant}
                                  onFocus={() => clearNameEditing(index)}
                                  onChange={(e) =>
                                    updateEntry(
                                      index,
                                      "variant",
                                      e.target.value,
                                    )
                                  }
                                  className="block h-[48px] w-full min-w-0 truncate rounded-xl border border-[#93ca87]/40 bg-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#24c3be]/25"
                                  disabled={isUploading}
                                />
                              </div>
                            )}

                            {/* -------- NOTES FIELD -------- */}
                            {entry.showNotesField && (
                              <div className="relative min-w-0 md:col-span-2">
                                <div className="mb-1 flex items-center justify-between gap-3">
                                  <div className="text-xs font-semibold text-slate-600">
                                    Notes
                                  </div>

                                  <div className="flex items-center gap-3">
                                    <span className="text-[10px] text-slate-400">
                                      {(entry.notes || "").length}/2000
                                    </span>

                                    <button
                                      type="button"
                                      onClick={() =>
                                        setEntries((prev) =>
                                          prev.map((e, i) =>
                                            i === index
                                              ? {
                                                  ...e,
                                                  showNotesField: false,
                                                }
                                              : e,
                                          ),
                                        )
                                      }
                                      className="text-xs text-slate-400 transition hover:text-red-500"
                                      title="Remove Notes field"
                                    >
                                      &#x2715;
                                    </button>
                                  </div>
                                </div>

                                <textarea
                                  value={entry.notes || ""}
                                  onChange={(e) =>
                                    updateEntry(
                                      index,
                                      "notes",
                                      e.target.value.slice(0, 2000),
                                    )
                                  }
                                  onFocus={() => clearNameEditing(index)}
                                  placeholder="Add field observations, behavior, habitat details, or anything else relevant to this photo."
                                  rows={4}
                                  maxLength={2000}
                                  disabled={isUploading}
                                  className="w-full min-w-0 resize-y rounded-xl border border-[#93ca87]/40 bg-white px-4 py-3 text-sm text-[#374049] focus:outline-none focus:ring-2 focus:ring-[#24c3be]/25"
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="bioindex-upload-editor-bottom-nav">
                        <button
                          type="button"
                          disabled={
                            isUploading ||
                            index === 0 ||
                            entries[index - 1]?.uploadStatus === "success"
                          }
                          onClick={() => selectUploadPhoto(index - 1)}
                          className="bioindex-upload-previous-photo"
                        >
                          <span aria-hidden="true">‹</span> Previous Photo
                        </button>
                        <span className="bioindex-upload-draft-note">
                          Details are kept as you switch photos
                        </span>
                        {index < entries.length - 1 ? (
                          <button
                            type="button"
                            disabled={isUploading}
                            onClick={() => selectUploadPhoto(index + 1)}
                            className="bioindex-upload-next-photo"
                          >
                            Next Photo <span aria-hidden="true">→</span>
                          </button>
                        ) : (
                          <span className="bioindex-upload-last-photo-note">
                            Last photo. Use Upload below when ready.
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </div>
          )}
          {/* One shared action: Upload becomes Stop upload while transferring. */}
          {entries.length > 0 && !uploadSuccess &&
            (uploadView === "editing" || uploadView === "progress") && (
              <div className="bioindex-upload-actions">
                <button
                  type="button"
                  onClick={isUploading ? stopCurrentUpload : handleUpload}
                  className="bioindex-upload-primary-cta"
                  style={isUploading ? { background: "#374049", color: "#ffffff", boxShadow: "none" } : undefined}
                >
                  {isUploading
                    ? "Stop upload"
                    : entries.some((entry) => entry.uploadStatus === "success")
                      ? "Resume Upload"
                      : "Upload"}
                </button>
              </div>
            )}
        </div>
        {/* The tutorial uses the same overlay and responsive sheet as Shared Details. */}
        {tutorialOpen && (
          <div
            className="bioindex-upload-shared-overlay bioindex-upload-tutorial-overlay"
            role="presentation"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) setTutorialStep(null);
            }}
          >
            <div
              ref={tutorialModalRef}
              className="bioindex-upload-shared-modal bioindex-upload-tutorial-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="bioindex-upload-tutorial-title"
              aria-describedby="bioindex-upload-tutorial-description"
            >
              <div className="bioindex-upload-shared-modal-header">
                <div>
                  <span className="bioindex-upload-shared-kicker">
                    Step {tutorialStep + 1} of {TUTORIAL_STEPS.length}
                  </span>
                  <h2 id="bioindex-upload-tutorial-title">Upload tutorial</h2>
                </div>
                <button
                  type="button"
                  data-tutorial-close
                  onClick={() => setTutorialStep(null)}
                  className="bioindex-upload-shared-close"
                  aria-label="Close upload tutorial"
                  title="Close tutorial"
                >
                  ×
                </button>
              </div>

              <div className="bioindex-upload-shared-modal-body bioindex-upload-tutorial-body">
                <div className="bioindex-upload-tutorial-step">
                  <span
                    className="bioindex-upload-tutorial-step-number"
                    aria-hidden="true"
                  >
                    {tutorialStep + 1}
                  </span>
                  <div className="bioindex-upload-tutorial-step-content">
                    <h3>{TUTORIAL_STEPS[tutorialStep].title}</h3>
                    <p id="bioindex-upload-tutorial-description">
                      {TUTORIAL_STEPS[tutorialStep].text}
                    </p>
                  </div>
                </div>
                <div
                  className="bioindex-upload-tutorial-indicators"
                  aria-label={`Tutorial step ${tutorialStep + 1} of ${TUTORIAL_STEPS.length}`}
                >
                  {TUTORIAL_STEPS.map((step, index) => (
                    <span
                      key={step.title}
                      className={index === tutorialStep ? "is-current" : ""}
                      aria-hidden="true"
                    />
                  ))}
                </div>
              </div>

              <div className="bioindex-upload-shared-modal-footer bioindex-upload-tutorial-footer">
                <button
                  type="button"
                  onClick={() =>
                    setTutorialStep((step) => Math.max(0, step - 1))
                  }
                  disabled={tutorialStep === 0}
                  className="bioindex-upload-shared-secondary"
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={() =>
                    tutorialStep === TUTORIAL_STEPS.length - 1
                      ? setTutorialStep(null)
                      : setTutorialStep((step) => step + 1)
                  }
                  className="bioindex-upload-shared-primary"
                >
                  {tutorialStep === TUTORIAL_STEPS.length - 1
                    ? "Finish tutorial"
                    : "Next"}
                </button>
              </div>
            </div>
          </div>
        )}
        {sharedDetailsOpen && locationModal?.scope !== "global" && (
          <div
            className="bioindex-upload-shared-overlay"
            role="presentation"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget)
                setSharedDetailsOpen(false);
            }}
          >
            <div
              className="bioindex-upload-shared-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="bioindex-shared-modal-heading"
            >
              <div className="bioindex-upload-shared-modal-header">
                <div>
                  <span className="bioindex-upload-shared-kicker">
                    {sharedStep === 4 ? "Done" : `Step ${sharedStep} of 3`}
                  </span>
                  <h2 id="bioindex-shared-modal-heading">Shared details</h2>
                </div>
                <button
                  type="button"
                  aria-label="Close Shared Details"
                  title="Close"
                  onClick={() => setSharedDetailsOpen(false)}
                  className="bioindex-upload-shared-close"
                >
                  ×
                </button>
              </div>
              <div className="bioindex-upload-shared-modal-body">
                {sharedStep === 1 && (
                  <section>
                    <h3>What would you like to share?</h3>
                    <div className="bioindex-upload-shared-field-list">
                      {[
                        ["species", "Scientific name", "Identification"],
                        ["date", "Date", "Observation date"],
                        ["location", "Location", "Place of observation"],
                      ].map(([key, label, description]) => (
                        <button
                          key={key}
                          type="button"
                          aria-pressed={sharedField === key}
                          onClick={() => {
                            setSharedField(key);
                            setSharedModalError("");
                          }}
                          className={`bioindex-upload-shared-field ${sharedField === key ? "is-selected" : ""}`}
                        >
                          <span
                            className="bioindex-upload-shared-radio"
                            aria-hidden="true"
                          />
                          <span>
                            <strong>{label}</strong>
                            <small>{description}</small>
                          </span>
                        </button>
                      ))}
                    </div>
                  </section>
                )}
                {sharedStep === 2 && (
                  <section>
                    <h3>Which photos should receive this detail?</h3>
                    <div className="bioindex-upload-shared-selection-actions">
                      <span>
                        {sharedSelectedIds.length} of {entries.length} selected
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          setSharedSelectedIds(
                            entries
                              .filter(
                                (entry) => entry.uploadStatus !== "success",
                              )
                              .map((entry) => entry.id),
                          )
                        }
                      >
                        Select all
                      </button>
                      <button
                        type="button"
                        onClick={() => setSharedSelectedIds([])}
                      >
                        Clear
                      </button>
                    </div>
                    <div className="bioindex-upload-shared-photo-list">
                      {entries.map((entry, index) => {
                        const checked = sharedSelectedIds.includes(entry.id);
                        return (
                          <label
                            key={entry.id}
                            className={`bioindex-upload-shared-photo ${checked ? "is-selected" : ""}`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={entry.uploadStatus === "success"}
                              onChange={() =>
                                setSharedSelectedIds((current) =>
                                  checked
                                    ? current.filter((id) => id !== entry.id)
                                    : [...current, entry.id],
                                )
                              }
                            />
                            <img src={entry.previewUrl} alt="" />
                            <span>
                              <strong>Photo {index + 1}</strong>
                              <small>
                                {entry.uploadStatus === "success"
                                  ? "Already uploaded"
                                  : entry.scientificName ||
                                    entry.file?.name ||
                                    "Not identified yet"}
                              </small>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </section>
                )}
                {sharedStep === 3 && (
                  <section>
                    <h3>
                      {sharedField === "species"
                        ? "Enter the scientific name"
                        : sharedField === "date"
                          ? "Choose the date"
                          : "Choose the location"}
                    </h3>
                    <p className="bioindex-upload-shared-summary">
                      This will update {sharedSelectedIds.length} selected photo
                      {sharedSelectedIds.length === 1 ? "" : "s"}.
                    </p>
                    {sharedField === "species" && (
                      <div className="bioindex-upload-shared-form">
                        <label>
                          Scientific name
                          <input
                            type="text"
                            autoComplete="off"
                            value={globalSpeciesQuery}
                            placeholder="Genus species"
                            onChange={(event) =>
                              updateGlobalSpeciesSuggestions(event.target.value)
                            }
                          />
                        </label>
                        {globalSpeciesSuggestions.length > 0 && (
                          <div className="bioindex-upload-shared-suggestions">
                            {globalSpeciesSuggestions
                              .slice(0, 25)
                              .map((species, index) => (
                                <button
                                  type="button"
                                  key={`${species.scientific}-${index}`}
                                  onClick={() => {
                                    setGlobalSpeciesQuery(species.scientific);
                                    setGlobalCommonName(species.common || "");
                                    setGlobalSpeciesSuggestions([]);
                                  }}
                                >
                                  <i>{species.scientific}</i>
                                  {species.common && (
                                    <small>{species.common}</small>
                                  )}
                                </button>
                              ))}
                          </div>
                        )}
                        <label>
                          Common name{" "}
                          <span className="bioindex-upload-optional">
                            (optional)
                          </span>
                          <input
                            type="text"
                            value={globalCommonName}
                            placeholder="Common name"
                            onChange={(event) =>
                              setGlobalCommonName(event.target.value)
                            }
                          />
                        </label>
                      </div>
                    )}
                    {sharedField === "date" && (
                      <div className="bioindex-upload-shared-date">
                        <label>
                          Day
                          <select
                            value={globalDate.day}
                            onChange={(event) =>
                              setGlobalDate((date) => ({
                                ...date,
                                day: event.target.value,
                              }))
                            }
                          >
                            {DAYS.map((day) => (
                              <option key={day} value={day}>
                                {day}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label>
                          Month
                          <select
                            value={globalDate.month}
                            onChange={(event) =>
                              setGlobalDate((date) => ({
                                ...date,
                                month: event.target.value,
                              }))
                            }
                          >
                            {MONTHS.map((month) => (
                              <option key={month.value} value={month.value}>
                                {month.label}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label>
                          Year
                          <select
                            value={globalDate.year}
                            onChange={(event) =>
                              setGlobalDate((date) => ({
                                ...date,
                                year: event.target.value,
                              }))
                            }
                          >
                            {YEARS.map((year) => (
                              <option key={year} value={year}>
                                {year}
                              </option>
                            ))}
                          </select>
                        </label>
                      </div>
                    )}
                    {sharedField === "location" && (
                      <button
                        type="button"
                        className="bioindex-upload-shared-location-picker"
                        onClick={() => setLocationModal({ scope: "global" })}
                      >
                        <span>
                          {globalLocation || "Select location on map"}
                        </span>
                        <span aria-hidden="true">⌖</span>
                      </button>
                    )}
                  </section>
                )}
                {sharedStep === 4 && (
                  <div
                    className="bioindex-upload-shared-complete"
                    role="status"
                  >
                    <span aria-hidden="true">✓</span>
                    <h3>{sharedAppliedMessage}</h3>
                    <p>
                      You can edit individual photos below, or apply another
                      shared detail.
                    </p>
                    <div className="bioindex-upload-shared-complete-actions">
                      <button type="button" onClick={addAnotherSharedDetail}>
                        Add another detail
                      </button>
                      <button
                        type="button"
                        onClick={() => setSharedDetailsOpen(false)}
                      >
                        Return to upload
                      </button>
                    </div>
                  </div>
                )}
                {sharedModalError && (
                  <p className="bioindex-upload-shared-error" role="alert">
                    {sharedModalError}
                  </p>
                )}
              </div>
              {sharedStep !== 4 && (
                <div className="bioindex-upload-shared-modal-footer">
                  <button
                    type="button"
                    className="bioindex-upload-shared-secondary"
                    onClick={() => {
                      if (sharedStep === 1) setSharedDetailsOpen(false);
                      else setSharedStep((step) => step - 1);
                      setSharedModalError("");
                    }}
                  >
                    {sharedStep === 1 ? "Cancel" : "Back"}
                  </button>
                  <button
                    type="button"
                    className="bioindex-upload-shared-primary"
                    onClick={() => {
                      if (sharedStep === 2 && sharedSelectedIds.length === 0) {
                        setSharedModalError("Select at least one photo.");
                        return;
                      }
                      if (sharedStep === 3) applySelectedSharedDetails();
                      else {
                        setSharedModalError("");
                        setSharedStep((step) => step + 1);
                      }
                    }}
                  >
                    {sharedStep === 3 ? "Apply to selected photos" : "Continue"}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        {locationModal && (
          <LocationPickerModal
            initialLocation={
              locationModal.scope === "global"
                ? globalLocationData
                : entries[locationModal.index]?.locationData
            }
            onClose={() => setLocationModal(null)}
            onConfirm={(locationData) => {
              if (!locationData) return;

              if (locationModal.scope === "global") {
                // The map only supplies the draft value. Selected entries are
                // updated later when the Shared Details wizard is confirmed.
                setGlobalLocation(locationData.name);
                setGlobalLocationData(locationData);
              }

              if (locationModal.scope === "entry") {
                setEntries((prev) =>
                  prev.map((entry, i) =>
                    i === locationModal.index
                      ? {
                          ...entry,
                          location: locationData.name,
                          locationData,
                        }
                      : entry,
                  ),
                );
              }

              setLocationModal(null);
            }}
          />
        )}
        {showMissingNameAlert &&
          entries.some((entry) => !entry.scientificName?.trim()) && (
            <div
              role="alert"
              className="fixed bottom-4 left-1/2 z-[70] flex w-[calc(100%-2rem)] max-w-md -translate-x-1/2 items-start gap-3 rounded-2xl border border-rose-200 bg-[#FCFFF6] px-4 py-3 text-sm text-[#374049] shadow-2xl"
            >
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-rose-100 font-bold text-rose-700">
                !
              </span>
              <span className="flex-1 leading-5">
                Write a scientific name for each photo before uploading. The
                highlighted fields still need a name.
              </span>
              <button
                type="button"
                onClick={() => setShowMissingNameAlert(false)}
                aria-label="Dismiss upload alert"
                className="rounded-lg px-1 text-[#374049]/60 hover:text-[#374049]"
              >
                &#x2715;
              </button>
            </div>
          )}

        {removePhotoDialog && (
          <div
            className="fixed inset-0 z-[80] flex items-center justify-center bg-[#374049]/65 p-4"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                setRemovePhotoDialog(null);
              }
            }}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="remove-photo-dialog-title"
              className="w-full max-w-sm rounded-3xl border border-[#93CA87]/50 bg-[#FCFFF6] p-5 shadow-2xl sm:p-6"
            >
              <h2
                id="remove-photo-dialog-title"
                className="text-2xl font-semibold text-[#374049]"
                style={{ fontFamily: "Cormorant Garamond" }}
              >
                Remove photo?
              </h2>

              <p className="mt-3 text-sm leading-6 text-[#374049]">
                Remove this photo from the current upload attempt? It will not
                be uploaded to BioIndex.
              </p>

              <div className="mt-5 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setRemovePhotoDialog(null)}
                  className="rounded-xl border border-[#93CA87]/50 px-4 py-2 text-sm font-semibold text-[#374049]"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={confirmRemoveEntry}
                  className="rounded-xl border !border-[#b91c1c] !bg-[#b91c1c] px-5 py-2 text-sm font-semibold !text-white transition hover:!border-[#991b1b] hover:!bg-[#991b1b]"
                >
                  Remove photo
                </button>
              </div>
            </div>
          </div>
        )}

        {geminiContextWarning && (
          <div
            className="fixed inset-0 z-[80] flex items-center justify-center bg-[#374049]/65 p-4"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                setGeminiContextWarning(null);
              }
            }}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="gemini-context-dialog-title"
              className="w-full max-w-sm rounded-3xl border border-[#93CA87]/50 bg-[#FCFFF6] p-5 shadow-2xl sm:p-6"
            >
              <div className="flex items-center gap-2">
                <img
                  src={geminiLogo}
                  alt=""
                  className="h-6 w-6 object-contain"
                />

                <h2
                  id="gemini-context-dialog-title"
                  className="text-2xl font-semibold text-[#374049]"
                  style={{ fontFamily: "Cormorant Garamond" }}
                >
                  Improve the identification
                </h2>
              </div>

              <p className="mt-3 text-sm leading-6 text-[#374049]">
                Gemini can identify the photo without all observation details,
                but the suggestion may be more accurate if you first provide{" "}
                <strong>
                  {formatMissingGeminiContext(
                    geminiContextWarning.missingContext,
                  )}
                </strong>
                .
              </p>

              <p className="mt-2 text-xs leading-5 text-[#374049]/70">
                These details can help distinguish visually similar species and
                narrow the possibilities.
              </p>

              <div className="mt-5 flex flex-wrap justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setGeminiContextWarning(null)}
                  className="rounded-xl border border-[#93CA87]/50 px-4 py-2 text-sm font-semibold text-[#374049]"
                >
                  Add details
                </button>

                <button
                  type="button"
                  onClick={() => {
                    const { entryId, missingContext } = geminiContextWarning;
                    setGeminiContextWarning(null);
                    runGeminiSuggestion(entryId, missingContext);
                  }}
                  className="rounded-xl bg-[#01767A] px-5 py-2 text-sm font-semibold text-white hover:bg-[#005f63]"
                >
                  Continue anyway
                </button>
              </div>
            </div>
          </div>
        )}

        {geminiDialog && (
          <div
            className="fixed inset-0 z-[70] flex items-center justify-center bg-[#374049]/65 p-4"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) setGeminiDialog(null);
            }}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="gemini-dialog-title"
              className="w-full max-w-sm rounded-3xl border border-[#93CA87]/50 bg-[#FCFFF6] p-5 shadow-2xl sm:p-6"
            >
              <div className="flex items-center gap-2">
                <img
                  src={geminiLogo}
                  alt=""
                  className="h-6 w-6 object-contain"
                />
                <h2
                  id="gemini-dialog-title"
                  className="text-2xl font-semibold text-[#374049]"
                  style={{ fontFamily: "Cormorant Garamond" }}
                >
                  {geminiDialog.accepted
                    ? "Gemini suggests"
                    : "Identification unavailable"}
                </h2>
              </div>
              {geminiDialog.accepted ? (
                <p className="mt-3 text-sm leading-6 text-[#374049]">
                  Gemini thinks this is{" "}
                  <strong className="italic">{geminiDialog.name}</strong>
                  {geminiDialog.commonName
                    ? ` (${geminiDialog.commonName})`
                    : ""}
                  . Review the identification before using it.
                </p>
              ) : (
                <p className="mt-3 text-sm leading-6 text-[#374049]">
                  {geminiDialog.name ? (
                    <>
                      Gemini suggested{" "}
                      <strong className="italic">{geminiDialog.name}</strong> (
                      {geminiDialog.rank}). BioIndex accepts Gemini IDs only at
                      genus or species level, so this name was not inserted.
                    </>
                  ) : (
                    geminiDialog.errorMessage ||
                    "Gemini could not suggest a genus or species for this photo."
                  )}
                </p>
              )}
              {geminiDialog.name && geminiDialog.missingContext?.length > 0 && (
                <div className="mt-4 rounded-2xl border border-[#93CA87]/45 bg-white/70 px-3.5 py-3 text-xs leading-5 text-[#374049]/80">
                  <strong className="text-[#374049]">Context note:</strong> this
                  identification was made without{" "}
                  {formatMissingGeminiContext(geminiDialog.missingContext)}.
                  Adding{" "}
                  {geminiDialog.missingContext.length === 1
                    ? "that detail"
                    : "those details"}{" "}
                  and trying again may improve the identification.
                </div>
              )}

              {geminiDialog.name && (
                <a
                  href={`https://www.inaturalist.org/search?q=${encodeURIComponent(geminiDialog.name)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 inline-flex items-center gap-2 text-xs font-semibold text-[#01767A] underline decoration-[#24C3BE] underline-offset-4"
                >
                  <img
                    src={inatLogo}
                    alt=""
                    className="h-4 w-4 object-contain"
                  />{" "}
                  Check on iNaturalist &#x2197;
                </a>
              )}
              <div className="mt-5 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setGeminiDialog(null)}
                  className="rounded-xl border border-[#93CA87]/50 px-4 py-2 text-sm font-semibold text-[#374049]"
                >
                  {geminiDialog.accepted ? "Cancel" : "Close"}
                </button>
                {geminiDialog.accepted && (
                  <button
                    type="button"
                    onClick={acceptGeminiSuggestion}
                    className="rounded-xl bg-[#01767A] px-5 py-2 text-sm font-semibold text-white hover:bg-[#005f63]"
                  >
                    Accept ID
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ================= MODALS: EXISTING FORMS MANAGEMENT ================= */}
        {/* Rendered outside main upload block to avoid layout conflicts */}
        {entries.map(
          (entry, index) =>
            openExistingIndex === index && // only open for the selected entry
            entry.speciesExists && // must be an existing species
            entry.existingForms.length > 0 ? ( // must have forms to show
              <div
                key={`modal-${entry.id}`}
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 sm:p-4"
              >
                {/* -------- MODAL CONTAINER -------- */}
                <div className="flex h-dvh w-screen flex-col overflow-hidden bg-[#FCFFF6] shadow-2xl sm:h-auto sm:max-h-[88vh] sm:max-w-5xl sm:rounded-3xl">
                  {/* -------- HEADER -------- */}
                  <div className="flex items-start justify-between gap-4 bg-[#374049] px-5 py-5 sm:px-6">
                    <div className="min-w-0">
                      <h3
                        className="text-2xl font-semibold leading-tight text-white"
                        style={{ fontFamily: "Cormorant Garamond" }}
                      >
                        Existing photos for <em>{entry.scientificName}</em>
                        {entry.family && (
                          <>
                            {" "}
                            <span className="text-white/70">
                              ({entry.family})
                            </span>
                          </>
                        )}
                      </h3>

                      <p className="mt-1 text-xs leading-5 text-white/65">
                        Select a photo to mark it for deletion. It will only be
                        removed after this batch is successfully uploaded.
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => setOpenExistingIndex(null)}
                      aria-label="Close existing photos"
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/20 bg-white/10 text-sm text-white transition hover:bg-white/20"
                    >
                      &#x2715;
                    </button>
                  </div>

                  {/* -------- GRID OF EXISTING FORMS -------- */}
                  <div className="grid h-[50vh] content-center items-start grid-cols-1 gap-5 overflow-y-auto bg-white pr-1 sm:grid-cols-2 lg:grid-cols-3">
                    {entry.existingForms.map((f, i) => (
                      <div
                        key={f.id || i}
                        onClick={() => toggleFormDeletion(index, f.id)} // toggle deletion state
                        className={`w-full max-w-[270px] justify-self-center cursor-pointer space-y-2 rounded-2xl border bg-[#f5f6f8] p-3 shadow-sm transition ${
                          (entry.formsToDelete || []).includes(f.id)
                            ? "border-red-500 ring-2 ring-red-100"
                            : "border-transparent hover:border-[#24C3BE]/60"
                        }`}
                      >
                        {/* -------- IMAGE PREVIEW -------- */}
                        <div className="flex justify-center rounded-xl bg-[#f5f6f8] p-2">
                          <img
                            src={f.url} // stored Firebase URL
                            alt="existing"
                            className="max-h-44 w-auto object-contain" // preserve aspect ratio
                          />
                        </div>

                        {/* -------- METADATA DISPLAY -------- */}
                        <div className="space-y-[2px] rounded-xl bg-white px-2.5 py-2 text-[10px] leading-tight text-[#374049]/80 shadow-sm">
                          {/* Lifeform label */}
                          {f.label && (
                            <div>
                              <span className="font-semibold text-[#01767A]">
                                Lifeform:{" "}
                              </span>
                              {f.label}
                            </div>
                          )}

                          {/* Date */}
                          {f.date && (
                            <div>
                              <span className="font-semibold text-[#01767A]">
                                Date:{" "}
                              </span>
                              {[f.date.day, f.date.month, f.date.year]
                                .filter((v) => v && v !== "NA")
                                .join("-")}
                            </div>
                          )}

                          {/* Location */}
                          {f.location && (
                            <div className="truncate">
                              <span className="font-semibold text-[#01767A]">
                                Location:{" "}
                              </span>
                              {f.location}
                            </div>
                          )}

                          {/* -------- COLLECTIONS (NORMALIZED) -------- */}
                          {(() => {
                            let cols = [];

                            if (Array.isArray(f.collections)) {
                              cols = f.collections
                                .map((c) => {
                                  if (typeof c === "string") return c;
                                  if (c && typeof c === "object")
                                    return c.name || c.title || c.id || "";
                                  return "";
                                })
                                .filter(Boolean);
                            } else if (typeof f.collections === "string") {
                              cols = [f.collections];
                            } else if (typeof f.collection === "string") {
                              cols = [f.collection];
                            } else if (Array.isArray(f.collectionNames)) {
                              cols = f.collectionNames.filter(Boolean);
                            } else if (typeof f.collectionName === "string") {
                              cols = [f.collectionName];
                            }

                            cols = [
                              ...new Set(
                                cols
                                  .map((c) => String(c).trim())
                                  .filter(Boolean),
                              ),
                            ];

                            if (cols.length === 0) {
                              return (
                                <div>
                                  <span className="font-semibold text-[#01767A]">
                                    Collections:{" "}
                                  </span>
                                  None
                                </div>
                              );
                            }

                            return (
                              <div>
                                <span className="font-semibold text-[#01767A]">
                                  Collections:{" "}
                                </span>

                                <div className="mt-1 flex flex-wrap gap-1">
                                  {cols.map((c) => (
                                    <span
                                      key={c}
                                      className="rounded-full border border-[#374049]/10 bg-[#f5f6f8] px-2 py-1 text-[10px] text-[#374049]"
                                    >
                                      {c}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            );
                          })()}
                        </div>

                        {/* -------- DELETION FLAG -------- */}
                        {(entry.formsToDelete || []).includes(f.id) && (
                          <div className="text-center text-[10px] text-red-600">
                            Marked for deletion
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* -------- FOOTER -------- */}
                  <div className="flex items-center justify-between gap-4 border-t border-[#93CA87]/35 bg-white px-4 py-3 sm:px-5">
                    <span className="text-xs text-[#374049]/60">
                      {(entry.formsToDelete || []).length} selected for deletion
                    </span>

                    <button
                      type="button"
                      onClick={() => setOpenExistingIndex(null)}
                      className="h-10 rounded-xl bg-[#374049] px-6 text-sm font-semibold text-white transition hover:bg-[#01767A]"
                    >
                      Done
                    </button>
                  </div>
                </div>
              </div>
            ) : null, // render nothing if conditions not met
        )}

        <SearchModal
          open={searchOpen}
          onClose={() => setSearchOpen(false)}
          uid={auth.currentUser?.uid}
        />

        {voiceOpen && (
          <SearchModal open voiceOnly onClose={() => setVoiceOpen(false)} uid={auth.currentUser?.uid} />
        )}
        <BioIndexUploadMobileNav onSearch={() => setSearchOpen(true)} onVoiceSearch={() => setVoiceOpen(true)} />
      </div>
    </div>
  );
}
